import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, MessageSquare, RefreshCw, Users, Bell, Plus, X, Pencil, Search, AlertTriangle, Mail, User, Trash2, AlignLeft, FileText, GripVertical } from "lucide-react";
import { format, parseISO, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { slackApi } from "@/api/slack";
import { useAuth } from "@/contexts/AuthContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { createOrUpdateReportForCheckIn, callCalculateReminders } from '@/utils/reportUtils';
// Add import for snapshotReportConfigToReport
import { snapshotReportConfigToReport } from '@/utils/reportUtils';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { formatInTimeZone } from "date-fns-tz";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { questionService } from "@/services/questionService";
import { ReportConfigEditor } from "@/components/check-ins/ReportConfigEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TimezoneSelect from 'react-timezone-select';
import { MessageCustomizationDialog } from '@/components/check-ins/MessageCustomizationDialog';
import { validateTemplate } from "@/utils/templateVariables";
import { DEFAULT_EMAIL_SUBJECT, DEFAULT_EMAIL_MESSAGE, DEFAULT_SLACK_MESSAGE } from "@/constants/reminderTemplates";
import { ScrollArea } from "@/components/ui/scroll-area";

// Type definitions
interface CheckIn {
  check_in_id: string;
  name: string;
  description: string | null;
  slug: string;
  template_name: string;
  template_id: string;
  cadence: 'daily' | 'weekly' | 'monthly' | 'one-off' | 'on-demand' | 'bi-weekly';
  days_of_week: string[] | null;
  scheduled_day: string | null;
  reminder_time: string;
  cutoff_time: string;
  timezone: string;
  timezone_reminder: boolean;
  next_report_date_time: string | null;
  created_at: string;
  communication_channel: 'slack' | 'email';
  slack_workspace_id: string | null;
  slack_notification_enabled: boolean;
  report_sends: 'email' | 'slack_channel' | 'slack_dm' | 'none';
  report_slack_channel_id: string | null;
  report_slack_dm_user_id: string | null;
  report_email_addresses: string[] | null;
  submission_alerts_enabled: boolean;
  is_active: boolean;
  min_reminder_offset_hours: number;
  one_off_due_date: string | null;
  active_question_ids?: string[];
  report_config?: ReportConfig | null;
  reminder_message_template: string;
  reminder_email_subject_template: string | null;
}

// Add a type for the report configuration
interface ReportConfig {
  intro?: {
    title: string;
    content: string;
  };
  reportSummary?: {
    title: string;
    content: string;
  };
  aiSummaries: AISummaryConfig[];
  customReportComponents: CustomReportComponent[];
  includeIndividualUpdatesComponent: boolean;
  version?: string;
}

interface AISummaryConfig {
  summaryId: string;
  displayName: string;
  summaryType: string;
  targetSectionKey: string;
  userInstructions?: string | null;
  sourceQuestionIds: string[];
}

interface CustomReportComponent {
  isEnabled: boolean;
  componentKey: string;
  displayOrder: number;
  titleOverride?: string | null;
  config?: Record<string, any> | null;
}

interface Employee {
  employee_id: string;
  name: string;
  email: string;
  phone: string;
  timezone: string;
  avatar_url?: string;
  slack_id?: string;
  display_name?: string;
  phone_number?: string;
}

interface DbQuestion {
  question_id?: string;
  header?: string;
  format?: 'string' | 'array' | 'object';
  questions?: any;
  prompt_instructions?: string;
  organization_id?: string | null;
  response_schema?: any;
  is_global?: boolean;
  // Optional fields when mapped from GraphQL
  id?: string;
  isCustomized?: boolean;
}

// Add Report and ReportInstanceConfig types
interface ReportInstanceConfig {
  version?: string;
  aiSummaries?: any[]; // Consider defining a more specific type if possible
  customReportComponents?: any[]; // Consider defining a more specific type if possible
  includeIndividualUpdatesComponent?: boolean;
  // Add any other properties that might be present in report_instance_config
}

interface Report {
  report_id: string;
  check_in_id: string;
  report_date: string;
  status: string;
  report_instance_config: ReportInstanceConfig | null;
  created_at: string;
  updated_at: string;
  // Add any other properties that a report object might have
}

// Timeline item type for Questions and Reporting section
interface TimelineItem {
  id: string;
  type: 'intro' | 'question' | 'report';
  title: string;
  content?: string;
  order: number;
  questionId?: string; // For linking to actual questions
  questionData?: DbQuestion; // For storing the full question data when selected
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to format timezone into abbreviated form with offset
const formatTimezone = (timezone: string): string => {
  try {
    // Timezone abbreviation mappings for common timezones
    const timezoneMap: Record<string, string> = {
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Phoenix': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Anchorage': 'AKST',
      'America/Adak': 'HST',
      'America/Honolulu': 'HST',
      'America/Toronto': 'EST',
      'America/Vancouver': 'PST',
      'America/Edmonton': 'MST',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Europe/Rome': 'CET',
      'Europe/Madrid': 'CET',
      'Asia/Dubai': 'GST',
      'Asia/Singapore': 'SGT',
      'Asia/Tokyo': 'JST',
      'Australia/Sydney': 'AEST',
      'Australia/Melbourne': 'AEST',
      'Australia/Perth': 'AWST'
    };
    
    // Check if we have a direct mapping
    if (timezoneMap[timezone]) {
      return timezoneMap[timezone];
    }

    // For unknown timezones, try to extract the abbreviation
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
      hour: 'numeric', // Include hour to ensure timezone appears
    });
    
    const formatted = formatter.format(date);
    const match = formatted.match(/[A-Z]{3,4}$/); // Look for 3-4 letter abbr at end
    
    if (match) {
      return match[0];
    }
    
    // If we didn't find a standard abbreviation, extract from GMT format
    const gmtFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: 'numeric',
    });
    
    const gmtFormatted = gmtFormatter.format(date);
    const gmtMatch = gmtFormatted.match(/GMT[+-]\d{1,2}/);
    
    if (gmtMatch) {
      return gmtMatch[0].replace('GMT', '');
    }
    
    // Last resort: use the region name
    return timezone.split('/').pop()?.replace('_', ' ') || timezone;
  } catch (error) {
    return timezone;
  }
};

// Update the notification form schema to handle email addresses
const notificationFormSchema = z.object({
  reminder_time: z.string(),
  timezone_reminder: z.boolean(),
  report_sends: z.enum(['email', 'slack_channel', 'slack_dm', 'none']),
  report_slack_channel_id: z.string().nullable(),
  report_slack_dm_user_id: z.string().nullable(),
  min_reminder_offset_hours: z.number().min(0).default(1),
  report_email_addresses_input: z.string().optional(),
  submission_alerts_enabled: z.boolean().default(false),
  reminder_message_template: z.string().min(1, "Message template is required"),
  reminder_email_subject_template: z.string().nullable().optional(),
}).refine((data) => {
  // If report delivery is email, require email addresses
  if (data.report_sends === 'email') {
    if (!data.report_email_addresses_input || !data.report_email_addresses_input.trim()) {
      return false;
    }
  }
  return true;
}, {
  message: "Email addresses are required when using email report delivery",
  path: ["report_email_addresses_input"],
}).refine((data) => {
  // Validate that message template contains required variables
  const validation = validateTemplate(data.reminder_message_template);
  return validation.valid;
}, {
  message: "Message template must include {{check_in_link}}",
  path: ["reminder_message_template"],
});

type NotificationFormValues = z.infer<typeof notificationFormSchema>;

// Add this after the NotificationFormValues type
const scheduleFormSchema = z.object({
  cutoff_time: z.string().optional(),
  days_of_week: z.array(z.string()).optional(),
  scheduled_day: z.union([z.number().min(1).max(31), z.null()]).optional(),
  timezone: z.string(),
  one_off_due_date: z.string().nullable().optional(),
});

type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;

// Helper function to check if a timezone is valid
const isValidTimezone = (timezone: string | null | undefined): boolean => {
  if (!timezone) return false;
  if (timezone.trim() === '') return false;
  
  try {
    // Check if timezone is valid by trying to create a formatter
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (e) {
    return false;
  }
};

// Helper to access timezone_reminder property consistently whether using GraphQL or DB property
const shouldUseParticipantTimezone = (checkIn: CheckIn | null): boolean => {
  if (!checkIn) return false;
  // Only use snake_case (DB) property name
  return checkIn.timezone_reminder ?? false;
};

// Add these new components after the utility functions and before the main component

// Component for rendering cadence-specific schedule details
interface CadenceScheduleInfoProps {
  checkIn: CheckIn;
}

const DailyScheduleInfo = ({ checkIn }: CadenceScheduleInfoProps) => {
  const daysText = checkIn.days_of_week && checkIn.days_of_week.length > 0 
    ? `Every ${checkIn.days_of_week.join(', ')}` 
    : 'No days selected';
    
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="font-medium text-sm">Cadence</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> 
          <span>{daysText}</span>
        </p>
      </div>
      
      <div className="space-y-1">
        <p className="font-medium text-sm">Check-Ins Due</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" /> 
          <span>
            {checkIn.cutoff_time ? 
              format(parseISO(`2000-01-01T${checkIn.cutoff_time}`), 'h:mm a') + ` ${formatTimezone(checkIn.timezone)}` :
              'Not set'
            }
          </span>
        </p>
      </div>
    </div>
  );
};

const WeeklyScheduleInfo = ({ checkIn }: CadenceScheduleInfoProps) => {
  const days = checkIn.days_of_week?.join(', ') || 'No days selected';
  const dayDisplay = checkIn.days_of_week?.length === 1 ? 
    checkIn.days_of_week[0] : // Single day display
    days; // Multiple days (legacy data) display
  
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="font-medium text-sm">Cadence</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> 
          <span>Weekly on {dayDisplay}</span>
        </p>
      </div>
      
      <div className="space-y-1">
        <p className="font-medium text-sm">Check-Ins Due</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" /> 
          <span>
            {checkIn.cutoff_time ? 
              format(parseISO(`2000-01-01T${checkIn.cutoff_time}`), 'h:mm a') + ` ${formatTimezone(checkIn.timezone)}` :
              'Not set'
            }
          </span>
        </p>
      </div>
    </div>
  );
};

const MonthlyScheduleInfo = ({ checkIn }: CadenceScheduleInfoProps) => {
  const dayText = checkIn.scheduled_day ? 
    `Day ${checkIn.scheduled_day} of each month` : 
    'No day selected';
    
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="font-medium text-sm">Cadence</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> 
          <span>{dayText}</span>
        </p>
      </div>
      
      <div className="space-y-1">
        <p className="font-medium text-sm">Check-Ins Due</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" /> 
          <span>
            {checkIn.cutoff_time ? 
              format(parseISO(`2000-01-01T${checkIn.cutoff_time}`), 'h:mm a') + ` ${formatTimezone(checkIn.timezone)}` :
              'Not set'
            }
          </span>
        </p>
      </div>
    </div>
  );
};

const OneOffScheduleInfo = ({ checkIn }: CadenceScheduleInfoProps) => {
  const dueDate = checkIn.one_off_due_date ? 
    format(parseISO(checkIn.one_off_due_date), 'PPP') : 
    'No due date set';
    
  const dueTime = checkIn.one_off_due_date ? 
    format(parseISO(checkIn.one_off_due_date), 'h:mm a') : 
    '';
  
  const timezone = checkIn.one_off_due_date ? formatTimezone(checkIn.timezone) : '';
    
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="font-medium text-sm">Cadence</p>
        <p className="text-muted-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> 
          <span>One-time check-in</span>
        </p>
      </div>
      
      {checkIn.one_off_due_date && (
        <>
          <div className="space-y-1">
            <p className="font-medium text-sm">Due Date</p>
            <p className="text-muted-foreground flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>{dueDate}</span>
            </p>
          </div>
          
          <div className="space-y-1">
            <p className="font-medium text-sm">Check-Ins Due</p>
            <p className="text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{dueTime} {timezone}</span>
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Component for rendering cadence-specific schedule form
interface CadenceScheduleFormProps {
  checkIn: CheckIn;
  scheduleForm: any; // Using any for simplicity, can be typed properly
}

const DailyScheduleForm = ({ scheduleForm }: CadenceScheduleFormProps) => (
  <>
    <FormField
      control={scheduleForm.control}
      name="cutoff_time"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Check-Ins Due</FormLabel>
          <FormDescription>
            Time when daily check-ins should be completed by
          </FormDescription>
          <FormControl>
            <Input
              type="time"
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    
    <FormField
      control={scheduleForm.control}
      name="days_of_week"
      render={() => (
        <FormItem>
          <div className="mb-4">
            <FormLabel>Days of Week</FormLabel>
            <FormDescription>
              Select which days check-ins are due.
            </FormDescription>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
              <FormField
                control={scheduleForm.control}
                name="days_of_week"
                key={day}
                render={() => (
                  <FormItem
                    key={day}
                    className="flex flex-row items-start space-x-3 space-y-0"
                  >
                    <FormControl>
                      <Checkbox
                        checked={scheduleForm.getValues('days_of_week')?.includes(day)}
                        onCheckedChange={(checked) => {
                          return checked
                            ? scheduleForm.setValue('days_of_week', [...(scheduleForm.getValues('days_of_week') || []), day])
                            : scheduleForm.setValue(
                                'days_of_week',
                                scheduleForm.getValues('days_of_week')?.filter(
                                  (value: string) => value !== day
                                )
                              )
                        }}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {day}
                    </FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  </>
);

const WeeklyScheduleForm = ({ scheduleForm }: CadenceScheduleFormProps) => (
  <>
    <FormField
      control={scheduleForm.control}
      name="cutoff_time"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Check-Ins Due</FormLabel>
          <FormDescription>
            Time when weekly check-ins should be completed by
          </FormDescription>
          <FormControl>
            <Input
              type="time"
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    
    <FormField
      control={scheduleForm.control}
      name="days_of_week"
      render={() => (
        <FormItem>
          <div className="mb-4">
            <FormLabel>Day of Week</FormLabel>
            <FormDescription>
              Select which day check-ins are due each week.
            </FormDescription>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
              <FormField
                control={scheduleForm.control}
                name="days_of_week"
                key={day}
                render={() => (
                  <FormItem
                    key={day}
                    className="flex flex-row items-start space-x-3 space-y-0"
                  >
                    <FormControl>
                      <Checkbox
                        checked={scheduleForm.getValues('days_of_week')?.includes(day)}
                        onCheckedChange={(checked) => {
                          // If checking this box, set days_of_week to just this day
                          if (checked) {
                            scheduleForm.setValue('days_of_week', [day]);
                          } else {
                            // If unchecking, remove this day (resulting in empty array)
                            scheduleForm.setValue('days_of_week', []);
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {day}
                    </FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  </>
);

const MonthlyScheduleForm = ({ scheduleForm }: CadenceScheduleFormProps) => (
  <>
    <FormField
      control={scheduleForm.control}
      name="cutoff_time"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Check-Ins Due</FormLabel>
          <FormDescription>
            Time when monthly check-ins should be completed by
          </FormDescription>
          <FormControl>
            <Input
              type="time"
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
    
    <FormField
      control={scheduleForm.control}
      name="scheduled_day"
      render={() => (
        <FormItem>
          <FormLabel>Day of month</FormLabel>
          <FormDescription>
            Select which day of the month to run
          </FormDescription>
          <Select
            onValueChange={(value) => {
              scheduleForm.setValue('scheduled_day', value === "none" ? null : Number(value));
            }}
            value={scheduleForm.getValues('scheduled_day')?.toString() || "none"}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select day of month" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">No specific day</SelectItem>
              {[...Array(28)].map((_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>
                  {i + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  </>
);

const OneOffScheduleForm = ({ scheduleForm }: CadenceScheduleFormProps) => (
  <FormField
    control={scheduleForm.control}
    name="one_off_due_date"
    render={({ field }) => (
      <FormItem className="flex flex-col">
        <FormLabel>Due Date & Time</FormLabel>
        <FormDescription>
          This is the complete deadline for the one-off check-in
        </FormDescription>
        <div className="space-y-3">
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground"
                  )}
                >
                  {field.value ? (
                    format(parseISO(field.value), "PPP")
                  ) : (
                    <span>Pick a date</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => {
                  // Preserve time if exists, otherwise defaults to 12:00
                  const currentValue = field.value ? new Date(field.value) : new Date();
                  const hours = currentValue.getHours();
                  const minutes = currentValue.getMinutes();
                  
                  if (date) {
                    const newDate = new Date(date);
                    newDate.setHours(hours);
                    newDate.setMinutes(minutes);
                    
                    // Update field with ISO string
                    field.onChange(newDate.toISOString());
                  } else {
                    field.onChange(null);
                  }
                }}
                disabled={(date) => date < addDays(new Date(), -1)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <FormItem>
            <FormLabel>Deadline Time</FormLabel>
            <FormControl>
              <Input
                type="time"
                onChange={(e) => {
                  if (!field.value) {
                    // If no date is set, use today
                    const now = new Date();
                    const [hours, minutes] = e.target.value.split(':');
                    now.setHours(parseInt(hours, 10));
                    now.setMinutes(parseInt(minutes, 10));
                    field.onChange(now.toISOString());
                  } else {
                    // Update time of existing date
                    const date = new Date(field.value);
                    const [hours, minutes] = e.target.value.split(':');
                    date.setHours(parseInt(hours, 10));
                    date.setMinutes(parseInt(minutes, 10));
                    field.onChange(date.toISOString());
                  }
                }}
                value={field.value ? format(new Date(field.value), 'HH:mm') : '12:00'}
              />
            </FormControl>
            <FormDescription>
              Set the exact time when the check-in will be due
            </FormDescription>
          </FormItem>
        </div>
        <FormMessage />
      </FormItem>
    )}
  />
);

// Component for rendering cadence-specific notification form
interface CadenceNotificationFormProps {
  checkIn: CheckIn;
  form: any; // Using any for simplicity, can be typed properly
  slackChannels: Array<{ id: string; name: string }>;
  isLoadingChannels: boolean;
  filteredEmployees: Employee[];
  isLoadingEmployees: boolean;
  employeeSearchTerm: string;
  handleEmployeeSearch: (searchValue: string, context: 'notifications' | 'participants') => void;
  onOpenMessageCustomization: () => void;
}

const DailyNotificationForm = ({ 
  form, 
  checkIn,
  slackChannels, 
  isLoadingChannels, 
  filteredEmployees, 
  isLoadingEmployees,
  employeeSearchTerm,
  handleEmployeeSearch,
  onOpenMessageCustomization
}: CadenceNotificationFormProps) => (
  <>
    <FormField
      control={form.control}
      name="reminder_time"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Reminder Time</FormLabel>
          <FormControl>
            <Input
              type="time"
              {...field}
            />
          </FormControl>
          <FormDescription>
            The system will try to send reminders at this time in the selected timezone.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="timezone_reminder"
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="text-base">Participant Timezones</FormLabel>
            <FormDescription>
              Send reminders in each participant's local timezone
            </FormDescription>
          </div>
          <FormControl>
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          </FormControl>
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="report_sends"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Report Delivery</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select where to send reports" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {checkIn.communication_channel === 'slack' ? (
                <>
                  <SelectItem value="slack_channel">Slack Channel</SelectItem>
                  <SelectItem value="slack_dm">Slack DM</SelectItem>
                </>
              ) : (
                <SelectItem value="email">Email</SelectItem>
              )}
              <SelectItem value="none">Don't Send Reports</SelectItem>
            </SelectContent>
          </Select>
          <FormDescription>
            Choose how you want to receive check-in reports.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />

    {form.watch('report_sends') === 'slack_channel' && (
      <FormField
        control={form.control}
        name="report_slack_channel_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Slack Channel</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || undefined}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a channel" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {isLoadingChannels ? (
                  <SelectItem value="_loading">Loading channels...</SelectItem>
                ) : slackChannels.length === 0 ? (
                  <SelectItem value="_none">No channels available</SelectItem>
                ) : (
                  slackChannels.map(channel => (
                    <SelectItem key={channel.id} value={channel.id}>
                      #{channel.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormDescription>
              Choose which Slack channel to send reports to.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    )}

    {form.watch('report_sends') === 'email' && (
      <>
        <FormField
          control={form.control}
          name="report_email_addresses_input"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Recipients</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter email addresses (comma-separated)"
                  type="text"
                />
              </FormControl>
              <FormDescription>
                Enter one or more email addresses separated by commas. Example: john@company.com, jane@company.com
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="submission_alerts_enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Send submission alerts
                </FormLabel>
                <FormDescription>
                  Get notified immediately when participants submit their check-ins
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
      </>
    )}

    {form.watch('report_sends') === 'slack_dm' && (
      <FormField
        control={form.control}
        name="report_slack_dm_user_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Slack User</FormLabel>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => handleEmployeeSearch(e.target.value, 'notifications')}
                  className="pl-8"
                />
              </div>
              <Select
                onValueChange={field.onChange}
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an employee" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingEmployees ? (
                    <SelectItem value="_loading">Loading employees...</SelectItem>
                  ) : filteredEmployees.length === 0 ? (
                    <SelectItem value="_none">
                      {employeeSearchTerm ? 'No matching employees found' : 'No employees with Slack accounts'}
                    </SelectItem>
                  ) : (
                    filteredEmployees.map(employee => (
                      <SelectItem key={employee.employee_id} value={employee.slack_id || ''}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={employee.avatar_url || undefined} />
                            <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{employee.name}</span>
                            <span className="text-xs text-muted-foreground">{employee.email}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <FormDescription>
              Choose which employee to send reports to via Slack DM.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    )}
    
    {/* Message Customization Button */}
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-medium">Message Templates</h4>
          <p className="text-xs text-muted-foreground">
            Customize reminder messages sent to participants
          </p>
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          onClick={onOpenMessageCustomization}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Customize Messages
        </Button>
      </div>
    </div>
  </>
);

const NonDailyNotificationForm = ({ 
  form, 
  checkIn,
  slackChannels, 
  isLoadingChannels, 
  filteredEmployees, 
  isLoadingEmployees,
  employeeSearchTerm,
  handleEmployeeSearch,
  onOpenMessageCustomization
}: CadenceNotificationFormProps) => (
  <>
    <FormField
      control={form.control}
      name="reminder_time"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Ideal Reminder Time</FormLabel>
          <FormControl>
            <Input
              type="time"
              {...field}
            />
          </FormControl>
          <FormDescription>
            The system will try to send reminders at this time in the selected timezone.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="min_reminder_offset_hours"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Minimum Reminder Notice</FormLabel>
          <div className="flex items-center">
            <FormControl>
              <Input
                type="number"
                min={0}
                step={1}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  // Ensure it's a valid number, otherwise default to 1
                  field.onChange(isNaN(value) ? 1 : value);
                }}
                onBlur={field.onBlur}
                value={field.value}
                ref={field.ref}
                className="mr-2"
              />
            </FormControl>
            <span className="text-sm text-muted-foreground">hours</span>
          </div>
          <FormDescription>
            Reminders will be sent at least this many hours before the cutoff time. The system will still try to use your ideal reminder time if possible.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="timezone_reminder"
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <FormLabel className="text-base">Participant Timezones</FormLabel>
            <FormDescription>
              Send reminders in each participant's local timezone
            </FormDescription>
          </div>
          <FormControl>
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          </FormControl>
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="report_sends"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Report Delivery</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select where to send reports" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {checkIn.communication_channel === 'slack' ? (
                <>
                  <SelectItem value="slack_channel">Slack Channel</SelectItem>
                  <SelectItem value="slack_dm">Slack DM</SelectItem>
                </>
              ) : (
                <SelectItem value="email">Email</SelectItem>
              )}
              <SelectItem value="none">Don't Send Reports</SelectItem>
            </SelectContent>
          </Select>
          <FormDescription>
            Choose how you want to receive check-in reports.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />

    {form.watch('report_sends') === 'slack_channel' && (
      <FormField
        control={form.control}
        name="report_slack_channel_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Slack Channel</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value || undefined}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a channel" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {isLoadingChannels ? (
                  <SelectItem value="_loading">Loading channels...</SelectItem>
                ) : slackChannels.length === 0 ? (
                  <SelectItem value="_none">No channels available</SelectItem>
                ) : (
                  slackChannels.map(channel => (
                    <SelectItem key={channel.id} value={channel.id}>
                      #{channel.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormDescription>
              Choose which Slack channel to send reports to.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    )}

    {form.watch('report_sends') === 'email' && (
      <>
        <FormField
          control={form.control}
          name="report_email_addresses_input"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Recipients</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter email addresses (comma-separated)"
                  type="text"
                />
              </FormControl>
              <FormDescription>
                Enter one or more email addresses separated by commas. Example: john@company.com, jane@company.com
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="submission_alerts_enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Send submission alerts
                </FormLabel>
                <FormDescription>
                  Get notified immediately when participants submit their check-ins
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
      </>
    )}

    {form.watch('report_sends') === 'slack_dm' && (
      <FormField
        control={form.control}
        name="report_slack_dm_user_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Slack User</FormLabel>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => handleEmployeeSearch(e.target.value, 'notifications')}
                  className="pl-8"
                />
              </div>
              <Select
                onValueChange={field.onChange}
                value={field.value || undefined}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an employee" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {isLoadingEmployees ? (
                    <SelectItem value="_loading">Loading employees...</SelectItem>
                  ) : filteredEmployees.length === 0 ? (
                    <SelectItem value="_none">
                      {employeeSearchTerm ? 'No matching employees found' : 'No employees with Slack accounts'}
                    </SelectItem>
                  ) : (
                    filteredEmployees.map(employee => (
                      <SelectItem key={employee.employee_id} value={employee.slack_id || ''}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={employee.avatar_url || undefined} />
                            <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{employee.name}</span>
                            <span className="text-xs text-muted-foreground">{employee.email}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <FormDescription>
              Choose which employee to send reports to via Slack DM.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    )}
    
    {/* Message Customization Button */}
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-medium">Message Templates</h4>
          <p className="text-xs text-muted-foreground">
            Customize reminder messages sent to participants
          </p>
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          onClick={onOpenMessageCustomization}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Customize Messages
        </Button>
      </div>
    </div>
  </>
);

// Add new type for new participants
interface NewParticipant {
  firstName: string;
  lastName: string;
  email: string;
  timezone: string;
  phoneNumber?: string;
}

export function CheckInDetails() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkIn, setCheckIn] = useState<CheckIn | null>(null);
  const [participants, setParticipants] = useState<Employee[]>([]);
  const [questions, setQuestions] = useState<DbQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isParticipantDialogOpen, setIsParticipantDialogOpen] = useState(false);
  const { user } = useAuth();
  
  // State variables we still need
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);
  const [slackChannels, setSlackChannels] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [employeesWithSlack, setEmployeesWithSlack] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAvailableEmployees, setFilteredAvailableEmployees] = useState<Employee[]>([]);
  const [isUpdating, setIsUpdating] = useState(false); // Added to manage loading state for the button

  // Add this after the isUpdating state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [latestReport, setLatestReport] = useState<Report | null>(null);
  const [allReports, setAllReports] = useState<Report[]>([]);

  // New state for Questions and Reporting section
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(true);
  const [selectedTimelineItem, setSelectedTimelineItem] = useState<TimelineItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<TimelineItem | null>(null);
  const [deleteTimelineItemId, setDeleteTimelineItemId] = useState<string | null>(null);
  const [isDeleteTimelineItemDialogOpen, setIsDeleteTimelineItemDialogOpen] = useState(false);
  const [isCreatingQuestion, setIsCreatingQuestion] = useState(false);
  const [isEditPanelVisible, setIsEditPanelVisible] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // State for question deletion functionality
  const [questionValidation, setQuestionValidation] = useState<{
    [questionId: string]: {
      canRemove: boolean;
      questionType: 'global' | 'custom';
      remainingQuestionsCount: number;
      pendingReportsCount: number;
      message: string;
    }
  }>({});
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(null);
  const [isReportConfigDialogOpen, setIsReportConfigDialogOpen] = useState(false);

  // State for message template customization
  const [isMessageTemplateDialogOpen, setIsMessageTemplateDialogOpen] = useState(false);
  const [selectedPreviewParticipant, setSelectedPreviewParticipant] = useState<Employee | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [senderName, setSenderName] = useState('');
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationFormSchema),
    defaultValues: {
      reminder_time: '',
      timezone_reminder: false,
      report_sends: 'none',
      report_slack_channel_id: null,
      report_slack_dm_user_id: null,
      min_reminder_offset_hours: 1,
      reminder_message_template: DEFAULT_SLACK_MESSAGE,
      reminder_email_subject_template: null,
    }
  });

  const scheduleForm = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      cutoff_time: checkIn?.cutoff_time || '',
      days_of_week: checkIn?.days_of_week || [],
      scheduled_day: checkIn?.scheduled_day ? Number(checkIn.scheduled_day) : null,
      timezone: checkIn?.timezone || '',
      one_off_due_date: checkIn?.one_off_due_date || null,
    }
  });

  // Add new state for new participants functionality
  const [newParticipants, setNewParticipants] = useState<NewParticipant[]>([{
    firstName: '',
    lastName: '',
    email: '',
    timezone: '',
    phoneNumber: ''
  }]);
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});
  const [isSubmittingNewParticipants, setIsSubmittingNewParticipants] = useState(false);
  const [activeTab, setActiveTab] = useState<'existing' | 'new'>('existing');

  // Add refs for notifications and participants cards
  const notificationsCardRef = useRef<HTMLDivElement>(null);
  const participantsCardRef = useRef<HTMLDivElement>(null);
  const [notificationsCardHeight, setNotificationsCardHeight] = useState<number | null>(null);

  // Use useMemo to compute notification info reactively
  const notificationInfo = useMemo(() => {
    if (!checkIn) return [];

    const info = [
      {
        label: "Reminder Time",
        value: checkIn.reminder_time ? format(parseISO(`2000-01-01T${checkIn.reminder_time}`), 'h:mm a') : "Not set",
        icon: <Clock className="h-4 w-4" />,
      },
    ];

    if (checkIn.cadence !== 'daily') {
      info.push({
        label: "Minimum Notice",
        value: `${checkIn.min_reminder_offset_hours || 1} hour${(checkIn.min_reminder_offset_hours || 1) !== 1 ? 's' : ''} before cutoff`,
        icon: <RefreshCw className="h-4 w-4" />,
      });
    }

    // Add the timezone reminder info
    info.push({
      label: "Timezone Setting",
      value: checkIn.timezone_reminder ? "Participant's local timezone" : `Check-in timezone (${formatTimezone(checkIn.timezone)})`,
      icon: <Bell className="h-4 w-4" />,
    });

    // Report delivery information
    if (checkIn.report_sends && checkIn.report_sends !== 'none') {
      if (checkIn.report_sends === 'slack_channel') {
        info.push({
          label: "Report Delivery",
          value: `Slack #${slackChannels.find(ch => ch.id === checkIn.report_slack_channel_id)?.name || 'Unknown Channel'}`,
          icon: <MessageSquare className="h-4 w-4" />,
        });
      } else if (checkIn.report_sends === 'slack_dm') {
        info.push({
          label: "Report Delivery",
          value: `Slack DM to ${employeesWithSlack.find((emp: Employee) => emp.slack_id === checkIn.report_slack_dm_user_id)?.display_name || 'Unknown User'}`,
          icon: <User className="h-4 w-4" />,
        });
      } else if (checkIn.report_sends === 'email') {
        const emailCount = checkIn.report_email_addresses?.length || 0;
        const emailText = emailCount === 1 
          ? checkIn.report_email_addresses?.[0] || ''
          : `${emailCount} email recipients`;
        
        info.push({
          label: "Report Delivery",
          value: emailText,
          icon: <Mail className="h-4 w-4" />,
        });

        // Add submission alerts info if enabled
        if (checkIn.submission_alerts_enabled) {
          info.push({
            label: "Submission Alerts",
            value: "Enabled",
            icon: <Bell className="h-4 w-4" />,
          });
        }
      }
    } else {
      info.push({
        label: "Report Delivery",
        value: "Disabled",
        icon: <X className="h-4 w-4" />,
      });
    }

    return info;
  }, [checkIn, slackChannels, employeesWithSlack]);

  // Effect to measure notifications card height
  useEffect(() => {
    if (notificationsCardRef.current) {
      setNotificationsCardHeight(notificationsCardRef.current.offsetHeight);
    }
  }, [notificationInfo, participants.length, checkIn]);

  useEffect(() => {
    const fetchCheckIn = async () => {
      try {
        // Fetch check-in details by slug
        const { data: checkInData, error: checkInError } = await supabase
          .from('check_ins')
          .select('*, active_question_ids')
          .eq('slug', slug)
          .single();

        if (checkInError) throw checkInError;
        setCheckIn(checkInData);

        // Fetch check-in participants
        const { data: participantsData, error: participantsError } = await supabase
          .from('check_in_employees')
          .select(`
            id,
            check_in_id,
            employee_id,
            next_reminder_send,
            employees (
              employee_id,
              name,
              email,
              phone_number,
              timezone,
              avatar_url,
              slack_id,
              first_name,
              last_name
            )
          `)
          .eq('check_in_id', checkInData.check_in_id);

        if (participantsError) throw participantsError;
        
        // Map the nested data to a flat structure
        const formattedParticipants: Employee[] = participantsData.map(item => {
          // Ensure employees is being accessed as an object, not an array
          const employeeData = item.employees as any;
          return {
            employee_id: employeeData.employee_id,
            name: employeeData.name,
            email: employeeData.email || '',
            phone: employeeData.phone_number || '',
            timezone: employeeData.timezone || '',
            avatar_url: employeeData.avatar_url,
            slack_id: employeeData.slack_id,
            // Use name as display_name if not available
            display_name: employeeData.name
          };
        });

        // Additional validation of timezone data
        formattedParticipants.forEach(participant => {
          if (!isValidTimezone(participant.timezone)) {
            // Log for debugging - this could be removed in production
            console.log(`Participant ${participant.name} has invalid timezone: "${participant.timezone}"`);
          }
        });

        setParticipants(formattedParticipants);

        // Fetch questions using active_question_ids
        const activeQuestionIds = checkInData.active_question_ids || [];
        if (activeQuestionIds.length === 0) {
          setQuestions([]);
        } else {
          const { data: questionsData, error: questionsError } = await supabase
            .from('questions')
            .select('question_id, header, format, questions, response_schema, prompt_instructions, organization_id, is_global, created_from_question_id')
            .in('question_id', activeQuestionIds);
          if (questionsError) throw questionsError;
          // Sort questions in the order of activeQuestionIds
          const sortedQuestions = activeQuestionIds.map((id: string) =>
            questionsData.find((q: any) => q.question_id === id)
          ).filter(Boolean);
          setQuestions(sortedQuestions);
        }

      } catch (error: any) {
        console.error('Error fetching check-in details:', error);
        toast.error('Failed to load check-in details');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchCheckIn();
    }
  }, [slug]);

  // Add this effect to fetch Slack data when component loads
  useEffect(() => {
    if (checkIn?.communication_channel === 'slack') {
      fetchSlackChannels();
      fetchEmployeesWithSlack();
    }
  }, [checkIn?.check_in_id]);

  // Update the useEffect for form reset
  useEffect(() => {
    if (checkIn && isNotificationDialogOpen) {
      // Convert email addresses array to comma-separated string for the form
      const emailAddressesInput = checkIn.report_email_addresses 
        ? checkIn.report_email_addresses.join(', ')
        : '';
      
      form.reset({
        reminder_time: checkIn.reminder_time,
        timezone_reminder: checkIn.timezone_reminder,
        report_sends: checkIn.report_sends,
        report_slack_channel_id: checkIn.report_slack_channel_id,
        report_slack_dm_user_id: checkIn.report_slack_dm_user_id,
        min_reminder_offset_hours: checkIn.min_reminder_offset_hours || 1,
        report_email_addresses_input: emailAddressesInput,
        submission_alerts_enabled: checkIn.submission_alerts_enabled || false,
        reminder_message_template: checkIn.reminder_message_template || (checkIn.communication_channel === 'email' ? DEFAULT_EMAIL_MESSAGE : DEFAULT_SLACK_MESSAGE),
        reminder_email_subject_template: checkIn.reminder_email_subject_template || (checkIn.communication_channel === 'email' ? DEFAULT_EMAIL_SUBJECT : null),
      });
    }
  }, [checkIn, isNotificationDialogOpen, form]);

  // Validate questions when timeline loads
  useEffect(() => {
    const validateQuestions = async () => {
      if (!checkIn || !timelineItems.length) return;
      
      const validations: typeof questionValidation = {};
      
      for (const item of timelineItems) {
        if (item.type === 'question' && item.questionId) {
          try {
            const validation = await questionService.canRemoveQuestionFromCheckIn(
              item.questionId,
              checkIn.check_in_id
            );
            validations[item.questionId] = validation;
          } catch (error) {
            console.error('Error validating question:', error);
            // Set default validation for error cases
            validations[item.questionId] = {
              canRemove: false,
              questionType: 'global',
              remainingQuestionsCount: 0,
              pendingReportsCount: 0,
              message: 'Validation failed'
            };
          }
        }
      }
      
      setQuestionValidation(validations);
    };
    
    validateQuestions();
  }, [timelineItems, checkIn]);

  // Initialize organization and sender name
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!user) return;
      
      try {
        // Get user's organization
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('organization_id, first_name, last_name')
          .eq('user_id', user.id)
          .single();

        if (userError) throw userError;

        if (userData?.organization_id) {
          // Get organization name
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('name')
            .eq('organization_id', userData.organization_id)
            .single();

          if (orgError) throw orgError;
          
          setOrganizationName(orgData?.name || '');
          setSenderName(`${userData.first_name || ''} ${userData.last_name || ''}`.trim() || user.email || 'Admin');
        }
      } catch (error) {
        console.error('Error fetching organization data:', error);
        setOrganizationName('Your Organization');
        setSenderName(user.email || 'Admin');
      }
    };

    fetchOrganizationData();
  }, [user]);

  // Set default preview participant when participants load
  useEffect(() => {
    if (participants.length > 0 && !selectedPreviewParticipant) {
      setSelectedPreviewParticipant(participants[0]);
    }
  }, [participants, selectedPreviewParticipant]);

  // Add function to validate email uniqueness
  const validateEmailUniqueness = async (email: string, organizationId: string): Promise<string | null> => {
    if (!email || !email.includes('@')) return null;
    
    try {
      const { data: existingEmployee } = await supabase
        .from('employees')
        .select('email, name')
        .eq('email', email.toLowerCase())
        .eq('organization_id', organizationId)
        .single();
      
      if (existingEmployee) {
        return `Email is already in use by ${existingEmployee.name}`;
      }
      return null;
    } catch (error) {
      // No existing employee found, which is good
      return null;
    }
  };

  // Add function to validate a single new participant
  const validateNewParticipant = async (participant: NewParticipant, index: number, organizationId: string | null): Promise<Record<string, string>> => {
    const errors: Record<string, string> = {};
    
    // Required field validation
    if (!participant.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!participant.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!participant.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participant.email)) {
      errors.email = 'Please enter a valid email address';
    } else {
      // Check for duplicates within the form
      const duplicateIndex = newParticipants.findIndex((p, i) => 
        i !== index && p.email.toLowerCase() === participant.email.toLowerCase()
      );
      if (duplicateIndex !== -1) {
        errors.email = `Duplicate email (also in participant ${duplicateIndex + 1})`;
      } else if (organizationId) {
        // Check for duplicates in database only if we have an organization ID
        const emailError = await validateEmailUniqueness(participant.email, organizationId);
        if (emailError) {
          errors.email = emailError;
        }
      }
    }
    
    if (!participant.timezone.trim()) {
      errors.timezone = 'Timezone is required';
    }
    
    return errors;
  };

  // Add function to handle new participant form changes
  const updateNewParticipant = (index: number, field: keyof NewParticipant, value: string) => {
    setNewParticipants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    
    // Clear validation error for this field
    setValidationErrors(prev => {
      const updated = { ...prev };
      if (updated[index]) {
        delete updated[index][field];
        if (Object.keys(updated[index]).length === 0) {
          delete updated[index];
        }
      }
      return updated;
    });
  };

  // Add function to validate field on blur
  const handleFieldBlur = async (index: number, field: keyof NewParticipant) => {
    const participant = newParticipants[index];
    
    // Get user's organization ID
    let organizationId: string | null = null;
    if (user) {
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        organizationId = userData?.organization_id || null;
      } catch (error) {
        console.error('Error fetching user organization:', error);
      }
    }
    
    const errors = await validateNewParticipant(participant, index, organizationId);
    
    setValidationErrors(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: errors[field] || ''
      }
    }));
  };

  // Add function to add new participant form
  const addNewParticipantForm = () => {
    setNewParticipants(prev => [...prev, {
      firstName: '',
      lastName: '',
      email: '',
      timezone: checkIn?.timezone || '',
      phoneNumber: ''
    }]);
  };

  // Add function to remove participant form
  const removeNewParticipantForm = (index: number) => {
    if (newParticipants.length > 1) {
      setNewParticipants(prev => prev.filter((_, i) => i !== index));
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[index];
        // Reindex remaining errors
        const reindexed: Record<number, Record<string, string>> = {};
        Object.keys(updated).forEach(key => {
          const oldIndex = parseInt(key);
          if (oldIndex > index) {
            reindexed[oldIndex - 1] = updated[oldIndex];
          } else if (oldIndex < index) {
            reindexed[oldIndex] = updated[oldIndex];
          }
        });
        return reindexed;
      });
    }
  };

  // Add function to handle new participants submission
  const handleAddNewParticipants = async () => {
    if (!checkIn || !user) return;
    
    setIsSubmittingNewParticipants(true);
    
    try {
      // Get user's organization ID first
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('user_id, organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (userError) throw userError;
      if (!userData?.organization_id) throw new Error('User organization not found');
      
      // Validate all participants
      const allErrors: Record<number, Record<string, string>> = {};
      let hasErrors = false;
      
      for (let i = 0; i < newParticipants.length; i++) {
        const errors = await validateNewParticipant(newParticipants[i], i, userData.organization_id);
        if (Object.keys(errors).length > 0) {
          allErrors[i] = errors;
          hasErrors = true;
        }
      }
      
      if (hasErrors) {
        setValidationErrors(allErrors);
        toast.error('Please fix the validation errors before continuing');
        return;
      }

      // Create employee records
      const employeeInserts = newParticipants.map(p => ({
        name: `${p.firstName.trim()} ${p.lastName.trim()}`,
        email: p.email.toLowerCase().trim(),
        timezone: p.timezone,
        organization_id: userData.organization_id,
        added_by_user_id: userData.user_id,
        first_name: p.firstName.trim(),
        last_name: p.lastName.trim(),
        phone_number: p.phoneNumber?.trim() || null,
        slack_id: null,
        avatar_url: null,
        role_title: null
      }));

      const { data: newEmployees, error: createError } = await supabase
        .from('employees')
        .insert(employeeInserts)
        .select('employee_id');

      if (createError) throw createError;
      if (!newEmployees || newEmployees.length === 0) {
        throw new Error('Failed to create employee records');
      }

      // Add new employees to check-in
      const checkInEmployeeInserts = newEmployees.map(emp => ({
        check_in_id: checkIn.check_in_id,
        employee_id: emp.employee_id
      }));

      const { error: addToCheckInError } = await supabase
        .from('check_in_employees')
        .insert(checkInEmployeeInserts);

      if (addToCheckInError) throw addToCheckInError;

      // Refresh participants list
      await refreshParticipants();
      
      // Calculate reminders if check-in is active
      if (checkIn.is_active) {
        for (const employee of newEmployees) {
          await callCalculateReminders(checkIn.check_in_id, employee.employee_id);
        }
      }
      
      // Reset form and close dialog
      setNewParticipants([{
        firstName: '',
        lastName: '',
        email: '',
        timezone: checkIn?.timezone || '',
        phoneNumber: ''
      }]);
      setValidationErrors({});
      setIsParticipantDialogOpen(false);
      setActiveTab('existing');
      
      toast.success(`Successfully added ${newParticipants.length} new participant${newParticipants.length > 1 ? 's' : ''}`);
      
    } catch (error: any) {
      console.error('Error adding new participants:', error);
      toast.error(`Failed to add participants: ${error.message}`);
    } finally {
      setIsSubmittingNewParticipants(false);
    }
  };

  // Update the handleOpenParticipantDialog function
  const handleOpenParticipantDialog = () => {
    // Reset new participants form
    setNewParticipants([{
      firstName: '',
      lastName: '',
      email: '',
      timezone: checkIn?.timezone || '',
      phoneNumber: ''
    }]);
    setValidationErrors({});
    setActiveTab(checkIn?.communication_channel === 'email' ? 'existing' : 'existing');
    setIsParticipantDialogOpen(true);
  };

  const handleRemoveParticipant = async (employeeId: string) => {
    if (!checkIn) return;

    try {
      // 1. Remove from check_in_employees
      const { error } = await supabase
        .from('check_in_employees')
        .delete()
        .eq('check_in_id', checkIn.check_in_id)
        .eq('employee_id', employeeId);

      if (error) throw error;

      // 2. Delete all check_in_tokens for this employee and check-in
      const { error: tokenDeleteError } = await supabase
        .from('check_in_tokens')
        .delete()
        .eq('check_in_id', checkIn.check_in_id)
        .eq('employee_id', employeeId);
      if (tokenDeleteError) {
        console.error('Error deleting check-in tokens for removed participant:', tokenDeleteError);
        toast.error('Failed to delete some tokens for this participant.');
      }

      // 3. Refresh participants to get the new count
      await refreshParticipants();
      toast.success('Participant removed successfully');

      // Check if this was the last participant and if the check-in is active
      // Need to access the updated participants count.
      // Assuming refreshParticipants updates a state variable like `participants`
      // The `participants` state might not update immediately after `refreshParticipants()` due to async nature of setState.
      // It's safer to fetch the count again or rely on the updated state in the next render cycle.
      // For immediate check, let's fetch the current count of participants for this check-in.
      const { count: remainingParticipantsCount, error: countError } = await supabase
        .from('check_in_employees')
        .select('*', { count: 'exact', head: true })
        .eq('check_in_id', checkIn.check_in_id);
      
      if (countError) {
        console.error("Error fetching remaining participant count:", countError);
        // Potentially show a toast, but don't block main flow
      }

      if (checkIn.is_active && remainingParticipantsCount === 0) {
        console.log(`Last participant removed from active check-in ${checkIn.check_in_id}. Deactivating.`);
        toast.info('Last participant removed. Deactivating check-in...');
        // Call the new deactivate-check-in Edge Function
        const { data: deactivateResult, error: deactivateError } = await supabase.functions.invoke('deactivate-check-in', {
          body: { checkInId: checkIn.check_in_id },
        });

        if (deactivateError) {
          console.error('Error deactivating check-in after last participant removed:', deactivateError);
          toast.error(`Failed to deactivate check-in: ${deactivateError.message}`);
        } else {
          console.log('Check-in deactivated after last participant removed:', deactivateResult);
          toast.success('Check-in automatically deactivated as it has no participants.');
          // Update local checkIn state
          setCheckIn(prev => (prev ? { ...prev, is_active: false, updated_at: new Date().toISOString() } : null));
          // Optionally, re-fetch check-in data or reports if needed
        }
      }

    } catch (error: any) {
      console.error('Error removing participant:', error);
      toast.error('Failed to remove participant');
    }
  };

  // Helper function to refresh the participants list
  const refreshParticipants = async () => {
    if (!checkIn) return; // Add null check
    
    try {
      const { data: participantsData, error: participantsError } = await supabase
        .from('check_in_employees')
        .select(`
          id,
          check_in_id,
          employee_id,
          next_reminder_send,
          employees (
            employee_id,
            name,
            email,
            phone_number,
            timezone,
            avatar_url,
            slack_id,
            first_name,
            last_name
          )
        `)
        .eq('check_in_id', checkIn.check_in_id);

      if (participantsError) throw participantsError;

      // Map the nested data to a flat structure
      const formattedParticipants: Employee[] = participantsData.map(item => {
        // Ensure employees is being accessed as an object, not an array
        const employeeData = item.employees as any;
        return {
          employee_id: employeeData.employee_id,
          name: employeeData.name,
          email: employeeData.email || '',
          phone: employeeData.phone_number || '',
          timezone: employeeData.timezone || '',
          avatar_url: employeeData.avatar_url,
          slack_id: employeeData.slack_id,
          // Use name as display_name if not available
          display_name: employeeData.name
        };
      });

      // Additional validation of timezone data
      formattedParticipants.forEach(participant => {
        if (!isValidTimezone(participant.timezone)) {
          // Log for debugging - this could be removed in production
          console.log(`Participant ${participant.name} has invalid timezone: "${participant.timezone}"`);
        }
      });

      setParticipants(formattedParticipants);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Failed to load participants');
    }
  };

  // Add this function to fetch Slack channels
  const fetchSlackChannels = async () => {
    if (!checkIn?.slack_workspace_id) return;
    
    setIsLoadingChannels(true);
    try {
      const response = await slackApi.listChannels();
      if (response.error) throw new Error(response.error);
      
      if (response.channels && Array.isArray(response.channels)) {
        setSlackChannels(response.channels.map(channel => ({
          id: channel.id,
          name: channel.name
        })));
      }
    } catch (error: any) {
      console.error('Error fetching Slack channels:', error);
      toast.error('Failed to load Slack channels');
    } finally {
      setIsLoadingChannels(false);
    }
  };

  // Modify the handleToggleStatus function to call calculate-reminders and create reports
  const handleToggleStatus = async () => {
    if (!checkIn) return;

    setIsUpdating(true); // Added to manage loading state for the button

    try {
      if (checkIn.is_active) {
        // User is deactivating the check-in
        console.log(`User initiated deactivation for check-in ${checkIn.check_in_id}`);
        const { data: deactivateResult, error: deactivateError } = await supabase.functions.invoke('deactivate-check-in', {
          body: { checkInId: checkIn.check_in_id },
        });

        if (deactivateError) {
          throw deactivateError;
        }
        console.log('Deactivate-check-in function result:', deactivateResult);
        setCheckIn(prev => (prev ? { ...prev, is_active: false, updated_at: new Date().toISOString() } : null));
        toast.success('Check-in paused successfully.');

      } else {
        // User is activating the check-in
        // Prevent activation if there are no participants
        if (participants.length === 0) {
          toast.error('Cannot activate check-in without participants. Please add participants first.');
          setIsUpdating(false);
          return;
        }
        
        // Original activation logic:
        const { error: updateError } = await supabase
          .from('check_ins')
          .update({ is_active: true, updated_at: new Date().toISOString() })
          .eq('check_in_id', checkIn.check_in_id);

        if (updateError) throw updateError;
        
        setCheckIn(prev => (prev ? { ...prev, is_active: true, updated_at: new Date().toISOString() } : null));

        // Create or update the report for this check-in
        const reportResult = await createOrUpdateReportForCheckIn(checkIn.check_in_id);
        console.log('DEBUG reportResult:', reportResult);
        console.log('DEBUG reportResult.data:', reportResult.data);
        if (!reportResult.success) {
          console.error('Error creating report during activation:', reportResult.error);
          toast.warning('Check-in activated, but there was an issue preparing its report.');
          // Not throwing error here, activation itself succeeded.
        } else {
          console.log('Report created successfully:', reportResult.data);
          
          // NEW: Snapshot report_config to report_instance_config if report was created
          if (reportResult.data && reportResult.data.reportId) {
            console.log('Calling snapshotReportConfigToReport with reportId:', reportResult.data.reportId);
            const snapshotResult = await snapshotReportConfigToReport(reportResult.data.reportId, checkIn.check_in_id);
            if (!snapshotResult.success) {
              console.warn('Failed to snapshot report_config to report_instance_config:', snapshotResult.error);
              toast.warning('Check-in activated, but report configuration was not copied to the report.');
            } else {
              console.log('Successfully snapshotted report_config to report_instance_config');
            }
          } else {
            console.warn('Cannot snapshot report_config: No reportId in response');
            toast.warning('Check-in activated, but there was an issue with the report configuration.');
          }
        }
        
        // Calculate reminders for participants
        const reminderResult = await callCalculateReminders(checkIn.check_in_id, undefined, true); // isActivation = true
        if (!reminderResult.success) {
          console.error('Error calculating reminders:', reminderResult.error);
          toast.warning('Check-in activated, but there was an issue setting up reminders.');
        } else {
          toast.success('Check-in activated successfully.');
        }
      }
    } catch (error: any) {
      console.error('Error toggling check-in status:', error);
      const action = checkIn.is_active ? 'pause' : 'activate';
      toast.error(`Failed to ${action} check-in: ${error.message}`);
    } finally {
      setIsUpdating(false); // Added to manage loading state
    }
  };

  // Modify the handleUpdateNotifications function to call calculate-reminders
  const handleUpdateNotifications = async (values: NotificationFormValues) => {
    if (!checkIn) return;

    try {
      // Parse and validate email addresses if report_sends is 'email'
      let emailAddresses: string[] = [];
      if (values.report_sends === 'email' && values.report_email_addresses_input) {
        const parsedEmails = parseEmailAddresses(values.report_email_addresses_input);
        const { valid, invalid } = validateEmailAddresses(parsedEmails);
        
        if (invalid.length > 0) {
          toast.error(`Invalid email addresses: ${invalid.join(', ')}`);
          return;
        }
        
        if (valid.length === 0) {
          toast.error('Please enter at least one valid email address for report delivery.');
          return;
        }
        
        emailAddresses = valid;
      }

      const updateData: any = {
        reminder_time: values.reminder_time,
        timezone_reminder: values.timezone_reminder,
        report_sends: values.report_sends,
        min_reminder_offset_hours: values.min_reminder_offset_hours,
        reminder_message_template: values.reminder_message_template,
        reminder_email_subject_template: values.reminder_email_subject_template,
      };

      // Set report delivery settings
      if (values.report_sends === 'slack_channel') {
        updateData.report_slack_channel_id = values.report_slack_channel_id;
        updateData.report_slack_dm_user_id = null;
        updateData.report_email_addresses = null;
        updateData.submission_alerts_enabled = false; // Reset when not using email
      } else if (values.report_sends === 'slack_dm') {
        updateData.report_slack_channel_id = null;
        updateData.report_slack_dm_user_id = values.report_slack_dm_user_id;
        updateData.report_email_addresses = null;
        updateData.submission_alerts_enabled = false; // Reset when not using email
      } else if (values.report_sends === 'email') {
        updateData.report_slack_channel_id = null;
        updateData.report_slack_dm_user_id = null;
        updateData.report_email_addresses = emailAddresses;
        updateData.submission_alerts_enabled = values.submission_alerts_enabled;
      } else {
        updateData.report_slack_channel_id = null;
        updateData.report_slack_dm_user_id = null;
        updateData.report_email_addresses = null;
        updateData.submission_alerts_enabled = false; // Reset when not using email
      }

      const { error } = await supabase
        .from('check_ins')
        .update(updateData)
        .eq('check_in_id', checkIn.check_in_id);

      if (error) throw error;

      // Refresh check-in data
      const { data: updatedCheckIn, error: refreshError } = await supabase
        .from('check_ins')
        .select('*')
        .eq('check_in_id', checkIn.check_in_id)
        .single();

      if (refreshError) throw refreshError;
      setCheckIn(updatedCheckIn);
      
      // Call calculate-reminders after updating notification settings
      if (checkIn.is_active) {
        await callCalculateReminders(checkIn.check_in_id);
      }
      
      toast.success('Notification settings updated');
      setIsNotificationDialogOpen(false);
    } catch (error: any) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification settings');
    }
  };

  // Update the fetchEmployeesWithSlack function
  const fetchEmployeesWithSlack = async () => {
    if (!checkIn || !user) return;
    
    setIsLoadingEmployees(true);
    try {
      // Get user's organization ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (userError) throw userError;
      if (!userData?.organization_id) throw new Error('User organization not found');

      // Get all employees with Slack IDs from the organization
      const { data: employees, error: employeesError } = await supabase
        .from('employees')
        .select('*')
        .eq('organization_id', userData.organization_id)
        .not('slack_id', 'is', null);
      
      if (employeesError) throw employeesError;

      // Create a Set of employee IDs that we already have from participants
      const participantIds = new Set(participants.map(p => p.employee_id));
      
      // Filter to only include those with a valid slack_id
      const validEmployees = (employees || []).filter(emp => emp.slack_id && emp.slack_id !== '');
      
      // Combine participants (who have Slack IDs) with other employees who have Slack IDs
      const allEmployeesWithSlack = [
        ...participants.filter(p => p.slack_id && p.slack_id !== ''),
        ...validEmployees.filter(emp => !participantIds.has(emp.employee_id))
      ];
      
      setEmployeesWithSlack(allEmployeesWithSlack);
      setFilteredEmployees(allEmployeesWithSlack);
    } catch (error: any) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees with Slack accounts');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Unified employee search function that handles both cases
  const handleEmployeeSearch = (searchValue: string, context: 'notifications' | 'participants') => {
    if (context === 'notifications') {
      setEmployeeSearchTerm(searchValue);
      
      if (!searchValue.trim()) {
        setFilteredEmployees(employeesWithSlack);
        return;
      }

      const lowerCaseSearch = searchValue.toLowerCase();
      const filtered = employeesWithSlack.filter(employee => {
        const name = employee.name.toLowerCase();
        const email = employee.email.toLowerCase();
        return name.includes(lowerCaseSearch) || email.includes(lowerCaseSearch);
      });
      
      setFilteredEmployees(filtered);
    } else {
      setSearchQuery(searchValue);
      
      if (!searchValue.trim()) {
        setFilteredAvailableEmployees(availableEmployees);
        return;
      }

      const lowerCaseSearch = searchValue.toLowerCase();
      const filtered = availableEmployees.filter(employee => {
        const name = employee.name.toLowerCase();
        const email = employee.email.toLowerCase();
        return name.includes(lowerCaseSearch) || email.includes(lowerCaseSearch);
      });
      
      setFilteredAvailableEmployees(filtered);
    }
  };

  // Add this effect after the other useEffects
  useEffect(() => {
    if (form.watch('report_sends') === 'slack_dm') {
      setEmployeeSearchTerm('');
      fetchEmployeesWithSlack();
    }
    // We only want to run this when report_sends changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch('report_sends')]);

  // Update the handleUpdateSchedule function
  const handleUpdateSchedule = async (values: ScheduleFormValues) => {
    try {
      if (!checkIn) return;
      
      // Start with basic updates that apply to all cadence types
      const updateData: Record<string, any> = {
        timezone: values.timezone,
      };
      
      // Only update these fields for non-one-off check-ins
      if (checkIn.cadence !== 'one-off') {
        updateData.cutoff_time = values.cutoff_time;
      }
      
      // Handle different cadence types
      if (checkIn.cadence === 'daily') {
        // Set days_of_week for daily cadence
        updateData.days_of_week = values.days_of_week && values.days_of_week.length > 0 
          ? values.days_of_week 
          : null;
      } else if (checkIn.cadence === 'weekly' || checkIn.cadence === 'bi-weekly') {
        // For weekly check-ins, ensure only one day of week is selected
        // If multiple are somehow selected, just take the first one
        updateData.days_of_week = values.days_of_week && values.days_of_week.length > 0 
          ? [values.days_of_week[0]] 
          : null;
      } else if (checkIn.cadence === 'monthly') {
        updateData.scheduled_day = values.scheduled_day;
        updateData.days_of_week = null;
        updateData.one_off_due_date = null;
      } else if (checkIn.cadence === 'one-off') {
        updateData.one_off_due_date = values.one_off_due_date;
        updateData.days_of_week = null;
        updateData.scheduled_day = null;
      } else if (checkIn.cadence === 'on-demand') {
        updateData.days_of_week = null;
        updateData.scheduled_day = null;
        updateData.one_off_due_date = null;
      }
      
      const { error } = await supabase
        .from('check_ins')
        .update(updateData)
        .eq('check_in_id', checkIn.check_in_id);
      
      if (error) throw error;
      
      // Update local state
      setCheckIn((prev) => prev ? { ...prev, ...updateData } : null);
      
      // If check-in is active, update report date and recalculate reminders
      if (checkIn.is_active) {
        console.log('[SCHEDULE] Check-in is active, updating report date and reminders');
        
        // First update the report date to match new schedule
        const reportResult = await createOrUpdateReportForCheckIn(checkIn.check_in_id);
        if (!reportResult.success) {
          console.error('[SCHEDULE] Error updating report:', reportResult.error);
          toast.warning('Schedule updated, but there was an issue updating the report date.');
        } else {
          console.log('[SCHEDULE] Successfully updated report date:', reportResult);
        }

        // Then recalculate reminders based on new schedule
        const reminderResult = await callCalculateReminders(checkIn.check_in_id, undefined, true);
        if (!reminderResult.success) {
          console.error('[SCHEDULE] Error calculating reminders:', reminderResult.error);
          toast.warning('Schedule updated, but there was an issue updating reminders.');
        } else {
          console.log('[SCHEDULE] Successfully updated reminders');
        }
      }
      
      toast.success("Schedule updated successfully");
      setIsScheduleDialogOpen(false);
    } catch (error: any) {
      console.error("[SCHEDULE] Error updating schedule:", error);
      toast.error(`Failed to update schedule: ${error.message}`);
    }
  };

  // Update the useEffect for form reset
  useEffect(() => {
    if (checkIn && isScheduleDialogOpen) {
      // Create a clean form values object with all necessary fields
      const formValues: ScheduleFormValues = {
        cutoff_time: checkIn.cutoff_time || '',
        days_of_week: checkIn.days_of_week || [],
        scheduled_day: checkIn.scheduled_day ? Number(checkIn.scheduled_day) : null,
        timezone: checkIn.timezone || '',
        one_off_due_date: checkIn.one_off_due_date || null,
      };
      
      // Use a single reset call with the complete object
      scheduleForm.reset(formValues);
    }
  }, [checkIn, isScheduleDialogOpen, scheduleForm]);

  // Add a debug effect to monitor form values
  useEffect(() => {
    const subscription = scheduleForm.watch((value) => {
      console.log('Form values changed:', value);
    });
    return () => subscription.unsubscribe();
  }, [scheduleForm]);

  // Function to fetch available employees when dialog opens
  const fetchAvailableEmployees = async () => {
    if (!checkIn || !user) return;
    
    setIsLoadingEmployees(true);
    try {
      // Get user's organization ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (userError) throw userError;
      if (!userData?.organization_id) throw new Error('User organization not found');

      // Get all employees from the organization
      const { data: employees, error: employeesError } = await supabase
        .from('employees')
        .select('*')
        .eq('organization_id', userData.organization_id);
      
      if (employeesError) throw employeesError;

      // Filter out employees that are already participants
      const participantIds = new Set(participants.map(p => p.employee_id));
      const availableEmps = (employees || []).filter(emp => !participantIds.has(emp.employee_id));
      
      setAvailableEmployees(availableEmps);
      setFilteredAvailableEmployees(availableEmps);
    } catch (error: any) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Modify the handleAddParticipants function to call calculate-reminders
  const handleAddParticipants = async () => {
    if (!checkIn || selectedEmployeeIds.length === 0) return;

    try {
      const newParticipants = selectedEmployeeIds.map(employeeId => ({
        check_in_id: checkIn.check_in_id,
        employee_id: employeeId
      }));

      const { error } = await supabase
        .from('check_in_employees')
        .insert(newParticipants);

      if (error) throw error;

      await refreshParticipants();
      setIsParticipantDialogOpen(false);
      setSelectedEmployeeIds([]);
      
      // Call calculate-reminders for each new participant
      if (checkIn.is_active) {
        for (const employeeId of selectedEmployeeIds) {
          await callCalculateReminders(checkIn.check_in_id, employeeId);
        }
      }
      
      toast.success('Participants added successfully');
    } catch (error: any) {
      console.error('Error adding participants:', error);
      toast.error('Failed to add participants');
    }
  };

  // Effect to fetch available employees when dialog opens
  useEffect(() => {
    if (isParticipantDialogOpen) {
      fetchAvailableEmployees();
      setSelectedEmployeeIds([]);
      setSearchQuery('');
    }
  }, [isParticipantDialogOpen]);

  // Add this function after handleAddParticipants
  const handleDeleteCheckIn = async () => {
    if (!checkIn) return;

    setIsDeleting(true);
    try {
      // First, delete all check_in_tokens associated with this check-in
      const { error: deleteTokensError } = await supabase
        .from('check_in_tokens')
        .delete()
        .eq('check_in_id', checkIn.check_in_id);

      if (deleteTokensError) {
        console.error("Error deleting associated check-in tokens:", deleteTokensError);
        // We should probably throw here, as failure to delete tokens might lead to orphaned data
        // or issues if reports are deleted but tokens pointing to them remain.
        // However, to maintain original partial-failure behavior for now, we'll log and continue.
        // For a more robust solution, this should throw.
      }

      // Second, delete all reports for this check-in
      const { error: deleteReportsError } = await supabase
        .from('reports')
        .delete()
        .eq('check_in_id', checkIn.check_in_id);

      if (deleteReportsError) {
        console.error("Error deleting associated reports:", deleteReportsError);
        throw deleteReportsError; // If reports can't be deleted, we should stop.
      }

      // Delete associated check_in_employees
      const { error: deleteEmployeesError } = await supabase
        .from('check_in_employees')
        .delete()
        .eq('check_in_id', checkIn.check_in_id);

      if (deleteEmployeesError) {
        console.error("Error deleting associated check-in employees:", deleteEmployeesError);
        throw deleteEmployeesError;
      }

      // Finally delete the check-in itself
      const { error: deleteCheckInError } = await supabase
        .from('check_ins')
        .delete()
        .eq('check_in_id', checkIn.check_in_id);

      if (deleteCheckInError) throw deleteCheckInError;

      toast.success('Check-in deleted successfully.');
      navigate('/check-ins'); // Navigate back to the list page
    } catch (error: any) {
      console.error('Error deleting check-in:', error);
      toast.error(`Failed to delete check-in: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false); // Close dialog regardless of success
    }
  };

  // Fetch latest report for this check-in
  useEffect(() => {
    const fetchLatestReport = async () => {
      if (!checkIn) return;
      const { data, error } = await supabase
        .from('reports')
        .select('report_id, check_in_id, report_date, status, report_instance_config, created_at, updated_at')
        .eq('check_in_id', checkIn.check_in_id)
        .order('report_date', { ascending: false })
        .limit(1)
        .single();
      if (!error && data) {
        setLatestReport(data as Report);
      } else {
        setLatestReport(null);
      }
    };
    if (checkIn) fetchLatestReport();
  }, [checkIn]);

  // Update the useEffect to fetch reports with better error handling
  useEffect(() => {
    const fetchReports = async () => {
      if (!checkIn) return;
      
      try {
        console.log('Fetching reports for check-in ID:', checkIn.check_in_id);
        
        const { data, error } = await supabase
          .from('reports')
          .select('report_id, check_in_id, report_date, status, report_instance_config, created_at, updated_at')
          .eq('check_in_id', checkIn.check_in_id)
          .order('report_date', { ascending: false });
        
        if (error) {
          console.error('Error fetching reports:', error);
          // Don't show a toast here as it's not critical for the user experience
          // Just set empty reports to allow the component to render
          setAllReports([]);
          setLatestReport(null);
          return;
        }
        
        if (data && data.length > 0) {
          console.log('Fetched reports:', data);
          setAllReports(data as Report[]);
          setLatestReport(data[0] ? data[0] as Report : null);
        } else {
          console.log('No reports found for this check-in');
          setAllReports([]);
          setLatestReport(null);
        }
      } catch (error) {
        console.error('Exception when fetching reports:', error);
        setAllReports([]);
        setLatestReport(null);
      }
    };
    
    if (checkIn) fetchReports();
  }, [checkIn]);

  // Function to fetch timeline data from database
  const fetchTimelineData = async () => {
    if (!checkIn) return;
    
    setIsLoadingTimeline(true);
    
    try {
      const timelineData: TimelineItem[] = [];
      
      // 1. Add the intro item - get from report_config if available
      const introItem: TimelineItem = {
        id: 'intro-1',
        type: 'intro',
        title: checkIn.report_config?.intro?.title || 'Introduction',
        content: checkIn.report_config?.intro?.content || 'Provide an overview of the check-in and what information you\'re looking for.',
        order: 0
      };
      timelineData.push(introItem);
      
      // 2. Add question items based on active_question_ids order
      if (checkIn.active_question_ids && checkIn.active_question_ids.length > 0) {
        // Map the questions to maintain the order specified in active_question_ids
        const questionMap = new Map(questions.map(q => [q.question_id, q]));
        
        // Create timeline items in the order of active_question_ids
        checkIn.active_question_ids.forEach((questionId, index) => {
          const question = questionMap.get(questionId);
          if (question) {
            timelineData.push({
              id: `question-${question.question_id!}`,
              type: 'question',
              title: question.header ?? "",
              questionId: question.question_id!,
              order: index + 1 // +1 because intro is at index 0
            });
          }
        });
      }
      
      // 3. Add the report summary item
      const reportItem: TimelineItem = {
        id: 'report-1',
        type: 'report',
        title: 'Report Summary',
        content: getReportConfigSummary(checkIn.report_config ?? null),
        order: timelineData.length
      };
      timelineData.push(reportItem);
      
      setTimelineItems(timelineData);
      
      // Select first item by default
      if (timelineData.length > 0 && !selectedTimelineItem) {
        setSelectedTimelineItem(timelineData[0]);
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      toast.error('Failed to load questions and reporting data');
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  // Update the useEffect to initialize timeline data from database
  useEffect(() => {
    if (checkIn) {
      fetchTimelineData();
    }
  }, [checkIn, questions, selectedTimelineItem]);

  // Handle URL parameters for opening dialogs
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dialog = params.get('dialog');
    
    if (dialog === 'report-config' && checkIn) {
      setIsReportConfigDialogOpen(true);
      // Remove the parameter from URL without triggering navigation
      const newParams = new URLSearchParams(location.search);
      newParams.delete('dialog');
      const newSearch = newParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, checkIn]);

  // Effect to focus the title input when selecting a timeline item
  useEffect(() => {
    if (selectedTimelineItem && titleInputRef.current) {
      // Short timeout to ensure the DOM is ready
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [selectedTimelineItem]);

  // Helper function to show current config summary
  const getReportConfigSummary = (reportConfig: ReportConfig | null): string => {
    if (!reportConfig) {
      return 'No configuration set';
    }
    
    const summaryCount = reportConfig.aiSummaries?.length || 0;
    const componentCount = reportConfig.customReportComponents?.filter(c => c.isEnabled).length || 0;
    
    if (summaryCount === 0 && componentCount === 0) {
      return 'No AI summaries or components configured';
    }
    
    return `${summaryCount} AI summaries, ${componentCount} custom components`;
  };

  // Function to handle selecting a timeline item
  const handleSelectTimelineItem = async (item: TimelineItem) => {
    // If it's a report type, open the report configuration editor
    if (item.type === 'report') {
      setIsReportConfigDialogOpen(true);
      return;
    }
    
    // If it's a question type, fetch the full question data
    if (item.type === 'question' && item.questionId) {
      try {
        const { data: questionData, error } = await supabase
          .from('questions')
          .select('question_id, header, format, questions, prompt_instructions, response_schema, organization_id, is_global, created_from_question_id')
          .eq('question_id', item.questionId)
          .single();
        
        if (error) throw error;
        
        if (questionData) {
          // Create a new item with the question data included
          const updatedItem = {
            ...item,
            questionData: questionData as DbQuestion
          };
          setSelectedTimelineItem(updatedItem);
        } else {
          setSelectedTimelineItem(item);
        }
      } catch (error) {
        console.error('Error fetching question data:', error);
        toast.error('Failed to load question details');
        setSelectedTimelineItem(item);
      }
    } else {
      setSelectedTimelineItem(item);
    }
    
    setIsEditPanelVisible(true);
  };

  // Function to handle updating a timeline item
  const handleUpdateTimelineItem = (id: string, updates: Partial<TimelineItem>) => {
    setTimelineItems(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, ...updates } 
          : item
      )
    );
    
    // Also update the selected item if it's the one being updated
    if (selectedTimelineItem?.id === id) {
      setSelectedTimelineItem(prev => prev ? { ...prev, ...updates } : null);
    }
  };
  
  // Function to save question changes back to database
  const saveQuestionChanges = async () => {
    if (!selectedTimelineItem?.questionData || selectedTimelineItem.type !== 'question' || !checkIn) {
      return;
    }

    try {
      // Validate main question
      const mainQuestion = getMainQuestion(selectedTimelineItem.questionData.questions);
      if (!mainQuestion.trim()) {
        toast.error('Please enter a main question before saving');
        return;
      }

      await questionService.saveQuestionWithTransaction(
        selectedTimelineItem.questionData.question_id!,
        selectedTimelineItem.questionData,
        checkIn.check_in_id
      );

      // Refresh data
      await fetchCheckInDetails();
      
      toast.success('Question saved successfully');
      setIsEditPanelVisible(false);
      
      setTimeout(() => {
        setSelectedTimelineItem(null);
      }, 300);

    } catch (error: any) {
      console.error('Error saving question:', error);
      toast.error(`Failed to save question: ${error.message || error}`);
    }
  };

  // Function to handle drag start
  const handleDragStart = (item: TimelineItem) => {
    setIsDragging(true);
    setDraggedItem(item);
  };

  // Function to handle drag over another item
  const handleDragOver = (e: React.DragEvent, targetItem: TimelineItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) return;
    
    // Change appearance of the target item
    e.currentTarget.classList.add('bg-accent/20');
  };

  // Function to handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-accent/20');
  };

  // Function to handle drop
  const handleDrop = async (e: React.DragEvent, targetItem: TimelineItem) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-accent/20');
    
    if (!draggedItem || draggedItem.id === targetItem.id || !checkIn || targetItem.type === 'intro') return;
    
    // Reorder the items but keep intro at position 0
    const reorderedItems = [...timelineItems];
    
    // Get the indices but ensure intro always stays at position 0
    const draggedIndex = reorderedItems.findIndex(item => item.id === draggedItem.id);
    const targetIndex = reorderedItems.findIndex(item => item.id === targetItem.id);
    
    // Remove the dragged item
    const [removed] = reorderedItems.splice(draggedIndex, 1);
    
    // Insert at the target position
    reorderedItems.splice(targetIndex, 0, removed);
    
    // Update the order property but ensure intro is always first
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      order: index
    }));
    
    // Update state
    setTimelineItems(updatedItems);
    
    // Update active_question_ids based on new order
    // Filter out intro and report items, and extract question IDs in the new order
    const newQuestionOrder = updatedItems
      .filter(item => item.type === 'question')
      .map(item => item.questionId) as string[];
    
    try {
      // Update the database with the new question order
      const { error } = await supabase
        .from('check_ins')
        .update({ active_question_ids: newQuestionOrder })
        .eq('check_in_id', checkIn.check_in_id);
      
      if (error) throw error;
      
      // Update the local checkIn state
      setCheckIn(prev => prev ? { ...prev, active_question_ids: newQuestionOrder } : null);
      
      toast.success('Question order updated');
    } catch (error) {
      console.error('Error updating question order:', error);
      toast.error('Failed to save the updated order');
    }
  };

  // Function to handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedItem(null);
  };

  // Function to delete a timeline item
  const handleDeleteTimelineItem = (id: string) => {
    setDeleteTimelineItemId(id);
    setIsDeleteTimelineItemDialogOpen(true);
  };

  // Function to confirm timeline item deletion
  const confirmDeleteTimelineItem = async () => {
    if (!deleteTimelineItemId) return;
    
    const itemToDelete = timelineItems.find(item => item.id === deleteTimelineItemId);
    
    if (itemToDelete?.type === 'question' && itemToDelete.questionId) {
      try {
        setIsDeletingQuestion(true);
        setDeletingQuestionId(itemToDelete.questionId);
        
        // Optimistic UI update
        setTimelineItems(prev => prev.filter(item => item.id !== deleteTimelineItemId));
        
        const result = await questionService.removeQuestionFromCheckIn(
          itemToDelete.questionId,
          checkIn!.check_in_id
        );
        
        // Show appropriate success message with details
        const reportsText = result.affectedReportsCount > 0 
          ? ` and updated ${result.affectedReportsCount} pending report${result.affectedReportsCount > 1 ? 's' : ''}`
          : '';
        
        if (result.wasDeleted) {
          toast.success(`Custom question deleted successfully${reportsText}`);
        } else {
          toast.success(`Question removed from check-in${reportsText}`);
        }
        
        // Refresh timeline data to ensure consistency
        await fetchTimelineData();
        
      } catch (error) {
        console.error('Error removing question:', error);
        
        // Rollback optimistic update
        await fetchTimelineData();
        
        // Show specific error messages
        if (error instanceof Error) {
          if (error.message.includes('last question')) {
            toast.error('Cannot remove the last question from check-in');
          } else if (error.message.includes('permission')) {
            toast.error('You do not have permission to remove this question');
          } else {
            toast.error('Failed to remove question. Please try again.');
          }
        } else {
          toast.error('Failed to remove question');
        }
      } finally {
        setIsDeletingQuestion(false);
        setDeletingQuestionId(null);
      }
    } else {
      // Handle non-question timeline items (existing logic)
      setTimelineItems(prev => prev.filter(item => item.id !== deleteTimelineItemId));
      toast.success('Item deleted successfully');
    }
    
    // Clear selection and close dialog
    if (selectedTimelineItem?.id === deleteTimelineItemId) {
      setSelectedTimelineItem(null);
      setIsEditPanelVisible(false);
    }
    setIsDeleteTimelineItemDialogOpen(false);
    setDeleteTimelineItemId(null);
  };

  // Add function to update report_config when the intro or report content is changed
  const saveReportConfig = async () => {
    if (!checkIn || !selectedTimelineItem) return;
    
    try {
      // Get the current report_config
      const currentConfig: ReportConfig = checkIn.report_config ?? {
        aiSummaries: [],
        customReportComponents: [],
        includeIndividualUpdatesComponent: false,
      };
      
      // Update the specific part based on item type
      let updatedConfig: ReportConfig;
      
      if (selectedTimelineItem.type === 'intro') {
        updatedConfig = {
          ...currentConfig,
          aiSummaries: currentConfig.aiSummaries ?? [],
          customReportComponents: currentConfig.customReportComponents ?? [],
          includeIndividualUpdatesComponent: currentConfig.includeIndividualUpdatesComponent ?? false,
          intro: {
            title: selectedTimelineItem.title,
            content: selectedTimelineItem.content || ''
          },
          version: '1.0'
        };
      } else if (selectedTimelineItem.type === 'report') {
        updatedConfig = {
          ...currentConfig,
          aiSummaries: currentConfig.aiSummaries ?? [],
          customReportComponents: currentConfig.customReportComponents ?? [],
          includeIndividualUpdatesComponent: currentConfig.includeIndividualUpdatesComponent ?? false,
          reportSummary: {
            title: selectedTimelineItem.title,
            content: selectedTimelineItem.content || ''
          },
          version: '1.0'
        };
      } else {
        // If it's a question, we don't need to update report_config
        return;
      }
      
      // Update the database
      const { error } = await supabase
        .from('check_ins')
        .update({ report_config: updatedConfig })
        .eq('check_in_id', checkIn.check_in_id);
      
      if (error) throw error;
      
      // Update the local checkIn state
      setCheckIn(prev => prev ? { ...prev, report_config: updatedConfig } : null);
      
      toast.success('Report configuration saved');
      
      // Hide the edit panel after saving
      setIsEditPanelVisible(false);
      
      // Clear selection after a short delay to allow animation to complete
      setTimeout(() => {
        setSelectedTimelineItem(null);
      }, 300);
    } catch (error) {
      console.error('Error saving report configuration:', error);
      toast.error('Failed to save the report configuration');
    }
  };

  // Temporary helper to refresh the page after certain mutations (e.g., resetting a customized question)
  const fetchCheckInDetails = async () => {
    // For now, do a full reload which will trigger all data fetching hooks again.
    window.location.reload();
  };

  // Helper to split main and follow-up questions from a DbQuestion.questions field
  function getMainAndFollowUps(questions: any): { main: string; followUps: string[] } {
    if (Array.isArray(questions)) {
      return {
        main: questions[0] || '',
        followUps: questions.slice(1) || [],
      };
    }
    if (typeof questions === 'object' && questions !== null) {
      // If using object structure, prefer mainQuestion property
      return {
        main: questions.mainQuestion || '',
        followUps: questions.followUps || [],
      };
    }
    // Fallback: treat as string
    return {
      main: questions || '',
      followUps: [],
    };
  }

  // Helper function to get main question text for validation
  function getMainQuestion(questions: any): string {
    return getMainAndFollowUps(questions).main;
  }

  // Handler for adding a new custom question
  const handleAddQuestion = async () => {
    if (!checkIn || !user) return;

    try {
      setIsCreatingQuestion(true);
      
      // Get user's organization ID
      const { data: userOrg, error: userOrgError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (userOrgError) throw userOrgError;
      if (!userOrg?.organization_id) {
        throw new Error('Unable to determine organization');
      }

      const result = await questionService.createCustomQuestion(
        checkIn.check_in_id,
        userOrg.organization_id,
        user.id
      );

      // Refresh timeline data
      await fetchTimelineData();
      
      // Auto-select new question for editing
      const newTimelineItem = {
        id: `question-${result.question.id}`,
        type: 'question' as const,
        title: result.question.header,
        order: timelineItems.length,
        questionId: result.question.id,
        questionData: {
          question_id: result.question.id,
          header: result.question.header,
          format: result.question.format.toLowerCase() as 'array',
          questions: result.question.questions,
          response_schema: result.question.responseSchema,
          organization_id: result.question.organizationId,
          prompt_instructions: undefined,
          is_global: false,
          isCustomized: true
        }
      };
      
      handleSelectTimelineItem(newTimelineItem);
      
      toast.success('Custom question created');
    } catch (error) {
      console.error('Error creating question:', error);
      toast.error('Failed to create question');
    } finally {
      setIsCreatingQuestion(false);
    }
  };

  // Helper function to parse and validate email addresses
  const parseEmailAddresses = (input: string): string[] => {
    if (!input || !input.trim()) return [];
    
    // Split by comma, trim whitespace, and filter out empty strings
    const emails = input
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
    
    // Remove duplicates
    return [...new Set(emails)];
  };

  // Helper function to validate email addresses
  const validateEmailAddresses = (emails: string[]): { valid: string[]; invalid: string[] } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid: string[] = [];
    const invalid: string[] = [];
    
    emails.forEach(email => {
      if (emailRegex.test(email)) {
        valid.push(email);
      } else {
        invalid.push(email);
      }
    });
    
    return { valid, invalid };
  };

  // Helper functions for message template management
  const handleVariableInsert = (variable: string, fieldRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>) => {
    if (fieldRef.current) {
      const textarea = fieldRef.current;
      const startPos = textarea.selectionStart;
      const endPos = textarea.selectionEnd;
      const currentValue = textarea.value;
      
      // Ensure we have valid positions
      if (startPos !== null && endPos !== null) {
        // Insert the variable at the cursor position
        const newValue = currentValue.substring(0, startPos) + variable + currentValue.substring(endPos);
        
        // Update the form field based on the ref type
        if (fieldRef === messageTextareaRef) {
          form.setValue('reminder_message_template', newValue);
        } else if (fieldRef === subjectInputRef) {
          form.setValue('reminder_email_subject_template', newValue);
        }
        
        // Set focus and cursor position after the inserted variable
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(startPos + variable.length, startPos + variable.length);
        }, 0);
      }
    }
  };

  const resetToDefaults = () => {
    const isEmail = checkIn?.communication_channel === 'email';
    
    form.setValue('reminder_message_template', isEmail ? DEFAULT_EMAIL_MESSAGE : DEFAULT_SLACK_MESSAGE);
    form.setValue('reminder_email_subject_template', isEmail ? DEFAULT_EMAIL_SUBJECT : null);
    
    toast.success('Templates reset to defaults');
  };

  const handlePreviewParticipantChange = (participantId: string) => {
    const participant = participants.find(p => p.employee_id === participantId);
    setSelectedPreviewParticipant(participant || null);
  };

  const handleMessageCustomizationSave = async (messageTemplate: string, subjectTemplate: string | null) => {
    try {
      // Update the form values
      form.setValue('reminder_message_template', messageTemplate);
      form.setValue('reminder_email_subject_template', subjectTemplate);
      
      // Optionally save to database immediately
      const values = {
        ...form.getValues(),
        reminder_message_template: messageTemplate,
        reminder_email_subject_template: subjectTemplate
      };
      
      await handleUpdateNotifications(values);
      toast.success('Message templates saved successfully');
    } catch (error) {
      console.error('Error saving message templates:', error);
      toast.error('Failed to save message templates');
    }
  };

  const handleOpenMessageCustomization = () => {
    setIsMessageTemplateDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading check-in details...</p>
        </div>
      </div>
    );
  }

  if (!checkIn) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <h2 className="text-xl font-semibold">Check-in not found</h2>
        <p className="text-muted-foreground">The check-in you're looking for doesn't exist or has been deleted.</p>
        <Button onClick={() => navigate('/check-ins')}>Back to Check-ins</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Dev Info Box - only in dev */}
      {import.meta.env.DEV && (
        <Accordion type="multiple" className="mb-4">
          <AccordionItem value="checkin-info">
            <AccordionTrigger>Check-in Info</AccordionTrigger>
            <AccordionContent>
              <div className="text-xs font-mono space-y-1">
                <div>Check-in ID: {checkIn.check_in_id}</div>
                <div>Slug: {checkIn.slug}</div>
                <div>Template ID: {checkIn.template_id}</div>
                <div>Cadence: {checkIn.cadence}</div>
                <div>Status: {checkIn.is_active ? 'Active' : 'Paused'}</div>
                <div>Timezone: {checkIn.timezone}</div>
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="latest-report">
            <AccordionTrigger>Latest Report Info</AccordionTrigger>
            <AccordionContent>
              {latestReport ? (
                <div className="text-xs font-mono space-y-1">
                  <div>Report ID: {latestReport.report_id}</div>
                  <div>Report Date (UTC): {latestReport.report_date}</div>
                  <div>Report Date ({checkIn.timezone}): {formatInTimeZone(parseISO(latestReport.report_date), checkIn.timezone, 'yyyy-MM-dd HH:mm zzz')}</div>
                  <div>Report Status: {latestReport.status}</div>
                  <div>Config Version: {latestReport.report_instance_config?.version || 'N/A'}</div>
                  <div className="mt-1">reportInstanceConfig:</div>
                  <pre className="whitespace-pre-wrap break-all bg-slate-200 p-2 rounded max-h-64 overflow-y-auto">
                    {latestReport.report_instance_config ? JSON.stringify(latestReport.report_instance_config, null, 2) : 'N/A'}
                  </pre>
                </div>
              ) : <div className="text-xs font-mono">No reports found.</div>}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="all-reports">
            <AccordionTrigger>All Reports for this Check-in</AccordionTrigger>
            <AccordionContent>
              <div className="overflow-x-auto">
                <table className="text-xs font-mono border-collapse w-full">
                  <thead>
                    <tr className="bg-slate-200">
                      <th className="p-1 border">Report ID</th>
                      <th className="p-1 border">Status</th>
                      <th className="p-1 border">Report Date (UTC)</th>
                      <th className="p-1 border">Report Date ({checkIn.timezone})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allReports.map(r => (
                      <tr key={r.report_id} className="odd:bg-slate-50">
                        <td className="p-1 border">{r.report_id}</td>
                        <td className="p-1 border">{r.status}</td>
                        <td className="p-1 border">{r.report_date}</td>
                        <td className="p-1 border">{formatInTimeZone(parseISO(r.report_date), checkIn.timezone, 'yyyy-MM-dd HH:mm zzz')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">{checkIn.name}</h1>
          </div>
          <p className="text-muted-foreground">
            Created on {format(parseISO(checkIn.created_at), 'PPP')}
          </p>
        </div>
        <div className="flex gap-2">
           <Button 
            variant="destructive" 
            size="sm" 
            onClick={() => setIsDeleteDialogOpen(true)} // Open delete dialog
            disabled={isDeleting} // Disable button while deleting
           >
             <Trash2 className="h-4 w-4 mr-1" /> Delete
           </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(`/check-ins/report/${checkIn.slug}`)}
          >
            View Reports
          </Button>
        </div>
      </div>

      {/* Add Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the "{checkIn?.name}" check-in? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)} 
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteCheckIn} 
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add a Status Card */}
      <Card className={cn(
        "border-l-4",
        checkIn.is_active 
          ? "border-l-green-500 bg-green-50 dark:bg-green-950/10" 
          : "border-l-gray-300 bg-gray-50 dark:bg-gray-900/20"
      )}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-full",
                checkIn.is_active 
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                  : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              )}>
                {checkIn.is_active 
                  ? <RefreshCw className="h-5 w-5" /> 
                  : <Clock className="h-5 w-5" />}
              </div>
              <div>
                <h3 className="font-medium">Status: <span className={checkIn.is_active ? "text-green-600 dark:text-green-400" : "text-gray-500"}>{checkIn.is_active ? "Active" : "Paused"}</span></h3>
                <p className="text-sm text-muted-foreground">
                  {checkIn.is_active 
                    ? "Reminders will be sent according to the schedule" 
                    : "No reminders will be sent until activated"}
                </p>
                {!checkIn.is_active && participants.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Participants required to activate this check-in
                  </p>
                )}
              </div>
            </div>
            <div className="w-full sm:w-auto flex items-center gap-3">
              <span className="text-sm font-medium">{checkIn.is_active ? "Pause" : "Activate"}</span>
              <Switch
                checked={checkIn.is_active}
                onCheckedChange={handleToggleStatus}
                disabled={isUpdating || (!checkIn.is_active && participants.length === 0)}
                aria-label="Toggle check-in status"
                className={cn(
                  checkIn.is_active 
                    ? "bg-green-500 data-[state=checked]:bg-green-500" 
                    : "data-[state=unchecked]:bg-gray-200 dark:data-[state=unchecked]:bg-gray-700"
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schedule Details */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Schedule</CardTitle>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  if (checkIn) {
                    scheduleForm.reset({
                      cutoff_time: checkIn.cutoff_time || '',
                      days_of_week: checkIn.days_of_week || [],
                      scheduled_day: checkIn.scheduled_day ? Number(checkIn.scheduled_day) : null,
                      timezone: checkIn.timezone || '',
                      one_off_due_date: checkIn.one_off_due_date || null,
                    });
                  }
                  setIsScheduleDialogOpen(true);
                }}
              >
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {checkIn && (
              <>
                {checkIn.cadence === 'daily' && (
                  <DailyScheduleInfo 
                    checkIn={checkIn} 
                  />
                )}
                
                {checkIn.cadence === 'weekly' && (
                  <WeeklyScheduleInfo 
                    checkIn={checkIn} 
                  />
                )}
                
                {checkIn.cadence === 'monthly' && (
                  <MonthlyScheduleInfo 
                    checkIn={checkIn} 
                  />
                )}
                
                {checkIn.cadence === 'one-off' && (
                  <OneOffScheduleInfo 
                    checkIn={checkIn} 
                  />
                )}
                
                {/* Fallback for other cadence types */}
                {!['daily', 'weekly', 'monthly', 'one-off'].includes(checkIn.cadence) && (
                  <div className="space-y-1">
                    <p className="font-medium text-sm">Cadence</p>
                    <p className="text-muted-foreground">{checkIn.cadence}</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card ref={notificationsCardRef} className="relative">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Notifications</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                if (checkIn) {
                  form.reset({
                    reminder_time: checkIn.reminder_time || '',
                    timezone_reminder: checkIn.timezone_reminder || false,
                    report_sends: checkIn.report_sends || 'none',
                    report_slack_channel_id: checkIn.report_slack_channel_id,
                    report_slack_dm_user_id: checkIn.report_slack_dm_user_id,
                    min_reminder_offset_hours: checkIn.min_reminder_offset_hours || 1,
                    report_email_addresses_input: checkIn.report_email_addresses ? checkIn.report_email_addresses.join(', ') : '',
                    submission_alerts_enabled: checkIn.submission_alerts_enabled || false,
                    reminder_message_template: checkIn.reminder_message_template || (checkIn.communication_channel === 'email' ? DEFAULT_EMAIL_MESSAGE : DEFAULT_SLACK_MESSAGE),
                    reminder_email_subject_template: checkIn.reminder_email_subject_template || (checkIn.communication_channel === 'email' ? DEFAULT_EMAIL_SUBJECT : null),
                  });
                }
                
                setIsNotificationDialogOpen(true);
                
                if (checkIn?.communication_channel === 'slack') {
                  fetchSlackChannels();
                  fetchEmployeesWithSlack();
                }
              }}>
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {notificationInfo.map((info, index) => (
              <div key={index} className="space-y-1">
                <p className="font-medium text-sm">{info.label}</p>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm flex items-center gap-2">
                    {info.icon}
                    <span>{info.value}</span>
                  </span>
                  {info.label === "Report Delivery" && checkIn?.report_sends !== 'none' && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "ml-auto",
                        checkIn?.report_sends === 'slack_channel' && "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
                        checkIn?.report_sends === 'slack_dm' && "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800",
                        checkIn?.report_sends === 'email' && "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800"
                      )}
                    >
                      {checkIn?.report_sends === 'slack_channel' ? 'Channel' : 
                       checkIn?.report_sends === 'slack_dm' ? 'DM' : 'Email'}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Participants */}
        <Card
          ref={participantsCardRef}
          className="relative flex flex-col overflow-hidden"
          style={
            notificationsCardHeight
              ? { height: notificationsCardHeight, maxHeight: notificationsCardHeight, minHeight: 0 }
              : { minHeight: 0 }
          }
        >
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Participants</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={handleOpenParticipantDialog}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden">
            {participants.length > 0 ? (
              <ScrollArea 
                className="h-full w-full"
                style={{ 
                  maxHeight: notificationsCardHeight 
                    ? `${notificationsCardHeight - 80}px` // Account for header (60px) + padding (20px)
                    : '400px' // Fallback height
                }}
              >
                <div className="space-y-2 pr-2">
                  {participants.map((participant) => (
                    <li
                      key={participant.employee_id}
                      className="flex items-center justify-between py-2 px-4 hover:bg-accent/5 rounded-md group list-none"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={participant.avatar_url || undefined} />
                          <AvatarFallback>{getInitials(participant.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{participant.name}</span>
                            {shouldUseParticipantTimezone(checkIn) && !isValidTimezone(participant.timezone) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center text-amber-500 dark:text-amber-400 cursor-help">
                                      <AlertTriangle className="h-4 w-4" />
                                      <span className="text-xs ml-1">Using check-in timezone</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>This participant has no valid timezone configured.</p>
                                    <p>Using check-in timezone ({checkIn.timezone}) as fallback.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {participant.slack_id && (
                            <p className="text-xs text-muted-foreground">@{participant.display_name}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-opacity"
                        onClick={() => handleRemoveParticipant(participant.employee_id)}
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Remove {participant.name}</span>
                      </Button>
                    </li>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">No participants added yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Questions and Reporting Section with Timeline */}
      <Card className="w-full mt-10">
        <CardHeader className="flex flex-row items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <CardTitle>Questions and Reporting</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Timeline (left side, ~60% with responsive width) */}
            <div className={cn(
              "w-full transition-all duration-300 ease-in-out relative", // Added relative positioning
              isEditPanelVisible ? "md:w-3/5" : "md:w-full"
            )}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Timeline</h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleAddQuestion}
                  disabled={isCreatingQuestion}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {isCreatingQuestion ? 'Creating...' : 'Add Question'}
                </Button>
              </div>
              {/* Loading state */}
              {isLoadingTimeline ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary mb-4"></div>
                  <p className="text-muted-foreground">Loading timeline items...</p>
                </div>
              ) : timelineItems.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
                  <p className="text-lg font-medium">No items yet</p>
                  <p className="text-muted-foreground mb-4">Create your first question or report component</p>
                  <Button 
                    onClick={handleAddQuestion}
                    disabled={isCreatingQuestion}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {isCreatingQuestion ? 'Creating...' : 'Add First Question'}
                  </Button>
                </div>
              ) : (
                /* Timeline Component */
                <div className="space-y-6 relative">
                  {/* Vertical line - only render when there are timeline items */}
                  {timelineItems.length > 0 && (
                    <div className="absolute left-[19px] top-7 bottom-0 w-[2px] bg-border/50 pointer-events-none" />
                  )}
                  {/* Timeline Items */}
                  {timelineItems.map((item) => (
                    <div key={item.id} className="relative flex gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center z-10">
                        {item.type === 'intro' && <AlignLeft className="h-5 w-5 text-primary" />}
                        {item.type === 'question' && <MessageSquare className="h-5 w-5 text-primary" />}
                        {item.type === 'report' && <FileText className="h-5 w-5 text-primary" />}
                      </div>
                      <div className="flex-1">
                        <Card 
                          className={cn(
                            "relative group hover:shadow-md transition-shadow cursor-pointer",
                            selectedTimelineItem?.id === item.id && "border-primary/30",
                            isDragging && draggedItem?.id === item.id && "opacity-50"
                          )}
                          onClick={() => handleSelectTimelineItem(item)}
                          draggable={item.type !== 'intro'} // Only make non-intro items draggable
                          onDragStart={item.type !== 'intro' ? () => handleDragStart(item) : undefined}
                          onDragOver={item.type !== 'intro' ? (e) => handleDragOver(e, item) : undefined}
                          onDragLeave={item.type !== 'intro' ? handleDragLeave : undefined}
                          onDrop={item.type !== 'intro' ? (e) => handleDrop(e, item) : undefined}
                          onDragEnd={item.type !== 'intro' ? handleDragEnd : undefined}
                        >
                          <div className={cn(
                            "absolute left-0 top-0 bottom-0 w-1 rounded-l-md",
                            selectedTimelineItem?.id === item.id ? "bg-primary" : "bg-primary/30"
                          )} />
                          <div 
                            className={cn(
                              "absolute top-3 -left-8 transition-opacity",
                              item.type !== 'intro' 
                                ? "opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing" 
                                : "hidden" // Hide drag handle for intro
                            )}
                            onMouseDown={item.type !== 'intro' ? () => handleDragStart(item) : undefined}
                          >
                            <GripVertical className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <CardHeader className="py-3 flex flex-row justify-between items-center">
                            <CardTitle className="text-base">
                              {item.type === 'question' ? (
                                (() => {
                                  // Find the question data for this item
                                  const question = questions.find(q => q.question_id === item.questionId);
                                  const main = question ? getMainAndFollowUps(question.questions).main : '';
                                  return main && main.trim() !== '' ? main : 'Untitled Question';
                                })()
                              ) : (
                                item.title
                              )}
                            </CardTitle>
                            {selectedTimelineItem?.id === item.id && item.type !== 'intro' && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      className={cn(
                                        "h-8 w-8 text-muted-foreground hover:text-destructive",
                                        isDeletingQuestion && deletingQuestionId === item.questionId && "opacity-50 cursor-not-allowed"
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTimelineItem(item.id);
                                      }}
                                      disabled={
                                        isDeletingQuestion ||
                                        (item.type === 'question' && 
                                         !!item.questionId && 
                                         !!questionValidation[item.questionId] &&
                                         !questionValidation[item.questionId].canRemove)
                                      }
                                    >
                                      {isDeletingQuestion && deletingQuestionId === item.questionId ? (
                                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {item.type === 'question' && item.questionId && questionValidation[item.questionId] ? (
                                      questionValidation[item.questionId].canRemove 
                                        ? `Remove ${questionValidation[item.questionId].questionType} question`
                                        : questionValidation[item.questionId].message
                                    ) : (
                                      'Delete item'
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </CardHeader>
                          {item.content && selectedTimelineItem?.id !== item.id && (
                            <CardContent className="py-2">
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {item.content}
                              </p>
                            </CardContent>
                          )}
                        </Card>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Vertical separator - only render when edit panel is actually visible */}
            {isEditPanelVisible && (
              <Separator 
                orientation="vertical" 
                className="hidden md:block h-auto"
              />
            )}
            {/* Editor Panel - only show if isEditPanelVisible and selectedTimelineItem are set */}
            {isEditPanelVisible && selectedTimelineItem && (
              <div
                className={cn(
                  "w-full md:w-2/5 transition-all duration-300 ease-in-out",
                  isEditPanelVisible
                    ? "block md:relative md:translate-x-0"
                    : "hidden md:block md:relative md:translate-x-0"
                )}
                style={{ position: isEditPanelVisible ? undefined : undefined }}
              >
                <div className="mb-4">
                  <h3 className="text-lg font-medium">
                    {selectedTimelineItem.type === 'intro'
                      ? 'Edit Introduction'
                      : selectedTimelineItem.type === 'question'
                        ? 'Edit Question'
                        : 'Edit Item'}
                  </h3>
                </div>
                <div className="space-y-4">
                  {/* Title field removed as requested */}
                  
                  {selectedTimelineItem.type === 'question' && selectedTimelineItem.questionData ? (
                    (() => {
                      const { main, followUps } = getMainAndFollowUps(selectedTimelineItem.questionData.questions);
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Label htmlFor="main-question">Main Question</Label>
                              {selectedTimelineItem.questionData.isCustomized && (
                                <Badge variant="secondary" className="ml-2">
                                  Customized
                                </Badge>
                              )}
                            </div>
                            {selectedTimelineItem.questionData.isCustomized && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    if (selectedTimelineItem.questionData?.question_id) {
                                      await questionService.resetCustomizedQuestion(selectedTimelineItem.questionData.question_id);
                                    }
                                    toast.success('Question reset to global version');
                                    // Refresh the questions list
                                    await fetchCheckInDetails();
                                  } catch (error) {
                                    console.error('Error resetting question:', error);
                                    toast.error('Failed to reset question');
                                  }
                                }}
                              >
                                Reset to Global Version
                              </Button>
                            )}
                          </div>
                          <Input
                            id="main-question"
                            placeholder="Enter the main question"
                            value={main}
                            onChange={e => {
                              const updatedQuestionData = { ...selectedTimelineItem.questionData };
                              let newQuestions;
                              if (Array.isArray(updatedQuestionData.questions)) {
                                newQuestions = [e.target.value, ...updatedQuestionData.questions.slice(1)];
                              } else if (typeof updatedQuestionData.questions === 'object' && updatedQuestionData.questions !== null) {
                                newQuestions = {
                                  ...updatedQuestionData.questions,
                                  mainQuestion: e.target.value,
                                };
                              } else {
                                newQuestions = e.target.value;
                              }
                              updatedQuestionData.questions = newQuestions;
                              setSelectedTimelineItem({ ...selectedTimelineItem, questionData: updatedQuestionData });
                            }}
                          />
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Follow-up Questions</Label>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const updatedQuestionData = { ...selectedTimelineItem.questionData };
                                  let newQuestions;
                                  if (Array.isArray(updatedQuestionData.questions)) {
                                    newQuestions = [...updatedQuestionData.questions, ''];
                                  } else if (typeof updatedQuestionData.questions === 'object' && updatedQuestionData.questions !== null) {
                                    newQuestions = {
                                      ...updatedQuestionData.questions,
                                      followUps: [
                                        ...(updatedQuestionData.questions.followUps || []),
                                        '',
                                      ],
                                    };
                                  } else if (typeof updatedQuestionData.questions === 'string') {
                                    newQuestions = [updatedQuestionData.questions, ''];
                                  } else {
                                    newQuestions = ['', ''];
                                  }
                                  updatedQuestionData.questions = newQuestions;
                                  setSelectedTimelineItem({ ...selectedTimelineItem, questionData: updatedQuestionData });
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" /> Add
                              </Button>
                            </div>
                            <div className="space-y-3">
                              {followUps.map((question, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                  <Input
                                    placeholder={`Follow-up question ${index + 1}`}
                                    value={question}
                                    onChange={e => {
                                      const updatedQuestionData = { ...selectedTimelineItem.questionData };
                                      let newQuestions;
                                      if (Array.isArray(updatedQuestionData.questions)) {
                                        newQuestions = [...updatedQuestionData.questions];
                                        newQuestions[index + 1] = e.target.value;
                                      } else if (typeof updatedQuestionData.questions === 'object' && updatedQuestionData.questions !== null) {
                                        newQuestions = {
                                          ...updatedQuestionData.questions,
                                          followUps: [...(updatedQuestionData.questions.followUps || [])],
                                        };
                                        newQuestions.followUps[index] = e.target.value;
                                      } else if (typeof updatedQuestionData.questions === 'string') {
                                        newQuestions = [updatedQuestionData.questions, ...followUps];
                                        newQuestions[index + 1] = e.target.value;
                                      } else {
                                        newQuestions = ['', e.target.value];
                                      }
                                      updatedQuestionData.questions = newQuestions;
                                      setSelectedTimelineItem({ ...selectedTimelineItem, questionData: updatedQuestionData });
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => {
                                      const updatedQuestionData = { ...selectedTimelineItem.questionData };
                                      let newQuestions;
                                      if (Array.isArray(updatedQuestionData.questions)) {
                                        newQuestions = [
                                          updatedQuestionData.questions[0],
                                          ...updatedQuestionData.questions.slice(1, index + 1),
                                          ...updatedQuestionData.questions.slice(index + 2),
                                        ];
                                      } else if (typeof updatedQuestionData.questions === 'object' && updatedQuestionData.questions !== null) {
                                        newQuestions = {
                                          ...updatedQuestionData.questions,
                                          followUps: [
                                            ...(updatedQuestionData.questions.followUps || []).slice(0, index),
                                            ...(updatedQuestionData.questions.followUps || []).slice(index + 1),
                                          ],
                                        };
                                      } else if (typeof updatedQuestionData.questions === 'string') {
                                        newQuestions = [updatedQuestionData.questions, ...followUps.slice(0, index), ...followUps.slice(index + 1)];
                                      } else {
                                        newQuestions = [''];
                                      }
                                      updatedQuestionData.questions = newQuestions;
                                      setSelectedTimelineItem({ ...selectedTimelineItem, questionData: updatedQuestionData });
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="prompt-instructions" className="text-xs text-muted-foreground">Prompt Instructions (optional)</Label>
                            <Input
                              id="prompt-instructions"
                              placeholder="Enter instructions for the AI about how to handle this question"
                              value={selectedTimelineItem.questionData.prompt_instructions || ""}
                              onChange={e => {
                                const updatedQuestionData = { ...selectedTimelineItem.questionData };
                                updatedQuestionData.prompt_instructions = e.target.value;
                                setSelectedTimelineItem({ ...selectedTimelineItem, questionData: updatedQuestionData });
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()
                  ) : selectedTimelineItem.type === 'intro' ? (
                      <div className="space-y-2">
                        <Label htmlFor="item-content">Content</Label>
                        <Input 
                          id="item-content" 
                          placeholder="Enter introduction text for this check-in..." 
                          value={selectedTimelineItem.content || ''}
                          onChange={(e) => handleUpdateTimelineItem(selectedTimelineItem.id, { content: e.target.value })}
                          autoFocus={selectedTimelineItem.type === 'intro'}
                        />
                      </div>
                  ) : (
                    <div className="p-4 bg-muted/30 rounded-md text-center">
                      <p className="text-muted-foreground">Question details could not be loaded</p>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsEditPanelVisible(false);
                        // Clear selection after animation completes
                        setTimeout(() => {
                          setSelectedTimelineItem(null);
                        }, 300);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => {
                        if (selectedTimelineItem.type === 'intro') {
                          saveReportConfig();
                        } else if (selectedTimelineItem.type === 'question' && selectedTimelineItem.questionData) {
                          saveQuestionChanges();
                        } else {
                          toast.success('Changes saved');
                          setIsEditPanelVisible(false);
                          // Clear selection after animation completes
                          setTimeout(() => {
                            setSelectedTimelineItem(null);
                          }, 300);
                        }
                      }}
                      disabled={
                        selectedTimelineItem.type === 'question' && 
                        selectedTimelineItem.questionData &&
                        !getMainQuestion(selectedTimelineItem.questionData.questions)?.trim()
                      }
                    >
                      Save Changes
                    </Button>
                  </div>

                  {selectedTimelineItem.type === 'intro' && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-3 text-sm text-muted-foreground">
                      <p>Standup Hiro will use this content to start the conversation with your team. Here are some examples:</p>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Thank the teammate for calling in today and tell them not to forget to provide X, Y, Z details</li>
                        <li>Explain that you're looking for updates on current projects, blockers, and upcoming plans</li>
                        <li>Let the team know this information will be compiled into a report that helps everyone stay aligned</li>
                      </ul>
                      <p className="font-medium pt-2 border-t border-border/30">
                        Important Note: Every conversation with Standup Hiro is unique. Standup Hiro won't say this introduction word for word.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete timeline item confirmation dialog */}
      <AlertDialog 
        open={isDeleteTimelineItemDialogOpen} 
        onOpenChange={setIsDeleteTimelineItemDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const item = timelineItems.find(i => i.id === deleteTimelineItemId);
                if (item?.type === 'question' && item.questionId) {
                  const validation = questionValidation[item.questionId];
                  return validation?.questionType === 'custom' 
                    ? 'Delete Custom Question?' 
                    : 'Remove Question from Check-in?';
                }
                return 'Are you sure?';
              })()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const item = timelineItems.find(i => i.id === deleteTimelineItemId);
                if (item?.type === 'question' && item.questionId) {
                  const validation = questionValidation[item.questionId];
                  const pendingReportsText = validation?.pendingReportsCount > 0 
                    ? ` It will also be removed from ${validation.pendingReportsCount} pending report${validation.pendingReportsCount > 1 ? 's' : ''}.`
                    : '';
                  
                  if (validation?.questionType === 'custom') {
                    return `This will permanently delete this custom question.${pendingReportsText} Historical reports will remain unchanged. This action cannot be undone.`;
                  } else {
                    return `This will remove this question from the check-in.${pendingReportsText} The original question will remain available for other check-ins, and historical reports will remain unchanged.`;
                  }
                }
                return 'This will permanently delete this item from the timeline. This action cannot be undone.';
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setDeleteTimelineItemId(null)}
              disabled={isDeletingQuestion}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteTimelineItem}
              disabled={isDeletingQuestion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingQuestion ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                  Processing...
                </div>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Participants Dialog */}
      <Dialog open={isParticipantDialogOpen} onOpenChange={setIsParticipantDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Add Participants</DialogTitle>
            <DialogDescription>
              {checkIn?.communication_channel === 'email' 
                ? 'Add people from your organization or create new participant records.'
                : 'Select people to add to this check-in.'
              }
            </DialogDescription>
          </DialogHeader>

          {checkIn?.communication_channel === 'email' ? (
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'existing' | 'new')} className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
                <TabsTrigger value="existing">Add Existing Participants</TabsTrigger>
                <TabsTrigger value="new">Add New Participants</TabsTrigger>
              </TabsList>
              
              <TabsContent value="existing" className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex flex-col h-full min-h-0">
                  <div className="flex-shrink-0 space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Add from existing participants</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        These people are already in your organization but not part of this check-in
                      </p>
                    </div>
                    
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search employees..."
                        value={searchQuery}
                        onChange={(e) => handleEmployeeSearch(e.target.value, 'participants')}
                        className="pl-8"
                      />
                    </div>
                  </div>

                  <div className="border rounded-md flex-1 min-h-0 mt-4">
                    {isLoadingEmployees ? (
                      <div className="flex items-center justify-center p-4">
                        <div className="flex flex-col items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                          <p className="text-sm text-muted-foreground">Loading employees...</p>
                        </div>
                      </div>
                    ) : filteredAvailableEmployees.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {searchQuery ? 'No matching employees found' : 'No employees available to add'}
                      </div>
                    ) : (
                      <div className="divide-y max-h-full overflow-y-auto">
                        {filteredAvailableEmployees.map((employee) => (
                          <div
                            key={employee.employee_id}
                            className="flex items-center space-x-4 p-4 hover:bg-accent/5"
                          >
                            <Checkbox
                              checked={selectedEmployeeIds.includes(employee.employee_id)}
                              onCheckedChange={(checked) => {
                                setSelectedEmployeeIds(prev =>
                                  checked
                                    ? [...prev, employee.employee_id]
                                    : prev.filter(id => id !== employee.employee_id)
                                );
                              }}
                            />
                            <div className="flex items-center gap-3 flex-1">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={employee.avatar_url || undefined} />
                                <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
                              </Avatar>
                              <div className="space-y-1">
                                <p className="text-sm font-medium leading-none">{employee.name}</p>
                                <p className="text-sm text-muted-foreground">{employee.email}</p>
                              </div>
                            </div>
                            <Badge variant="secondary">
                              {formatTimezone(employee.timezone)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsParticipantDialogOpen(false);
                        setSelectedEmployeeIds([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddParticipants}
                      disabled={selectedEmployeeIds.length === 0}
                    >
                      Add Selected ({selectedEmployeeIds.length})
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="new" className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex flex-col h-full min-h-0">
                  <div className="flex-shrink-0">
                    <h3 className="font-medium mb-2">Add new participants</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add people who haven't been added to your organization yet
                    </p>
                  </div>
                  
                  <div className="flex-1 min-h-0 overflow-y-auto pr-2">
                    <div className="space-y-6">
                      {newParticipants.map((participant, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">Participant {index + 1}</h4>
                            {newParticipants.length > 1 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeNewParticipantForm(index)}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`firstName-${index}`}>First Name *</Label>
                              <Input
                                id={`firstName-${index}`}
                                placeholder="John"
                                value={participant.firstName}
                                onChange={(e) => updateNewParticipant(index, 'firstName', e.target.value)}
                                onBlur={() => handleFieldBlur(index, 'firstName')}
                                className={validationErrors[index]?.firstName ? 'border-destructive' : ''}
                              />
                              {validationErrors[index]?.firstName && (
                                <p className="text-sm text-destructive">{validationErrors[index].firstName}</p>
                              )}
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor={`lastName-${index}`}>Last Name *</Label>
                              <Input
                                id={`lastName-${index}`}
                                placeholder="Smith"
                                value={participant.lastName}
                                onChange={(e) => updateNewParticipant(index, 'lastName', e.target.value)}
                                onBlur={() => handleFieldBlur(index, 'lastName')}
                                className={validationErrors[index]?.lastName ? 'border-destructive' : ''}
                              />
                              {validationErrors[index]?.lastName && (
                                <p className="text-sm text-destructive">{validationErrors[index].lastName}</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`email-${index}`}>Email Address *</Label>
                            <Input
                              id={`email-${index}`}
                              type="email"
                              placeholder="john.smith@company.com"
                              value={participant.email}
                              onChange={(e) => updateNewParticipant(index, 'email', e.target.value)}
                              onBlur={() => handleFieldBlur(index, 'email')}
                              className={validationErrors[index]?.email ? 'border-destructive' : ''}
                            />
                            {validationErrors[index]?.email && (
                              <p className="text-sm text-destructive">{validationErrors[index].email}</p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`timezone-${index}`}>Timezone *</Label>
                            <TimezoneSelect
                              value={participant.timezone}
                              onChange={(timezone) => {
                                const timezoneValue = typeof timezone === 'string' ? timezone : timezone.value;
                                updateNewParticipant(index, 'timezone', timezoneValue);
                              }}
                              className="timezone-select"
                            />
                            {validationErrors[index]?.timezone && (
                              <p className="text-sm text-destructive">{validationErrors[index].timezone}</p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`phoneNumber-${index}`}>Phone Number</Label>
                            <Input
                              id={`phoneNumber-${index}`}
                              type="tel"
                              placeholder="+1 555-0123"
                              value={participant.phoneNumber}
                              onChange={(e) => updateNewParticipant(index, 'phoneNumber', e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0 space-y-4 pt-4 border-t mt-4">
                    <Button
                      variant="outline"
                      onClick={addNewParticipantForm}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Participant
                    </Button>
                    
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsParticipantDialogOpen(false);
                          setNewParticipants([{
                            firstName: '',
                            lastName: '',
                            email: '',
                            timezone: checkIn?.timezone || '',
                            phoneNumber: ''
                          }]);
                          setValidationErrors({});
                        }}
                        disabled={isSubmittingNewParticipants}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddNewParticipants}
                        disabled={isSubmittingNewParticipants || newParticipants.every(p => 
                          !p.firstName.trim() || !p.lastName.trim() || !p.email.trim() || !p.timezone.trim()
                        )}
                      >
                        {isSubmittingNewParticipants ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current mr-2" />
                            Adding...
                          </>
                        ) : (
                          `Add Participants (${newParticipants.filter(p => 
                            p.firstName.trim() && p.lastName.trim() && p.email.trim() && p.timezone.trim()
                          ).length})`
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            // Original single-tab layout for non-email check-ins
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex flex-col h-full min-h-0">
                <div className="flex-shrink-0 space-y-4">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search employees..."
                      value={searchQuery}
                      onChange={(e) => handleEmployeeSearch(e.target.value, 'participants')}
                      className="pl-8"
                    />
                  </div>
                </div>

                <div className="border rounded-md flex-1 min-h-0 mt-4">
                  {isLoadingEmployees ? (
                    <div className="flex items-center justify-center p-4">
                      <div className="flex flex-col items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                        <p className="text-sm text-muted-foreground">Loading employees...</p>
                      </div>
                    </div>
                  ) : filteredAvailableEmployees.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {searchQuery ? 'No matching employees found' : 'No employees available to add'}
                    </div>
                  ) : (
                    <div className="divide-y max-h-full overflow-y-auto">
                      {filteredAvailableEmployees.map((employee) => (
                        <div
                          key={employee.employee_id}
                          className="flex items-center space-x-4 p-4 hover:bg-accent/5"
                        >
                          <Checkbox
                            checked={selectedEmployeeIds.includes(employee.employee_id)}
                            onCheckedChange={(checked) => {
                              setSelectedEmployeeIds(prev =>
                                checked
                                  ? [...prev, employee.employee_id]
                                  : prev.filter(id => id !== employee.employee_id)
                              );
                            }}
                          />
                          <div className="flex items-center gap-3 flex-1">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={employee.avatar_url || undefined} />
                              <AvatarFallback>{getInitials(employee.name)}</AvatarFallback>
                            </Avatar>
                            <div className="space-y-1">
                              <p className="text-sm font-medium leading-none">{employee.name}</p>
                              <p className="text-sm text-muted-foreground">{employee.email}</p>
                            </div>
                          </div>
                          <Badge variant="secondary">
                            {formatTimezone(employee.timezone)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsParticipantDialogOpen(false);
                      setSelectedEmployeeIds([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddParticipants}
                    disabled={selectedEmployeeIds.length === 0}
                  >
                    Add Selected ({selectedEmployeeIds.length})
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add this dialog after the Add Participants Dialog */}
      <Dialog open={isNotificationDialogOpen} onOpenChange={setIsNotificationDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Notification Settings</DialogTitle>
            <DialogDescription>
              Configure when and how notifications and reports are sent.
            </DialogDescription>
          </DialogHeader>

          {checkIn && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleUpdateNotifications)} className="space-y-6">
                {checkIn && (
                  <>
                    {checkIn.cadence === 'daily' && (
                      <DailyNotificationForm
                        checkIn={checkIn}
                        form={form}
                        slackChannels={slackChannels}
                        isLoadingChannels={isLoadingChannels}
                        filteredEmployees={filteredEmployees}
                        isLoadingEmployees={isLoadingEmployees}
                        employeeSearchTerm={employeeSearchTerm}
                        handleEmployeeSearch={handleEmployeeSearch}
                        onOpenMessageCustomization={handleOpenMessageCustomization}
                      />
                    )}
                    
                    {checkIn.cadence !== 'daily' && (
                      <NonDailyNotificationForm
                        checkIn={checkIn}
                        form={form}
                        slackChannels={slackChannels}
                        isLoadingChannels={isLoadingChannels}
                        filteredEmployees={filteredEmployees}
                        isLoadingEmployees={isLoadingEmployees}
                        employeeSearchTerm={employeeSearchTerm}
                        handleEmployeeSearch={handleEmployeeSearch}
                        onOpenMessageCustomization={handleOpenMessageCustomization}
                      />
                    )}
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsNotificationDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Save Changes</Button>
                </div>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add the Schedule Edit Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Schedule</DialogTitle>
            <DialogDescription>
              Configure when check-ins are due.
            </DialogDescription>
          </DialogHeader>

          {checkIn && (
            <Form {...scheduleForm}>
              <form onSubmit={scheduleForm.handleSubmit(handleUpdateSchedule)} className="space-y-6">
                <FormField
                  control={scheduleForm.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Timezone</FormLabel>
                      <FormDescription>
                        All scheduled times use this timezone
                      </FormDescription>
                      <FormControl>
                        <TimezoneSelect
                          value={field.value}
                          onChange={(timezone) => {
                            field.onChange(typeof timezone === 'string' ? timezone : timezone.value);
                          }}
                          className="timezone-select"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Render the appropriate form based on cadence */}
                {checkIn && (
                  <>
                    {checkIn.cadence === 'daily' && (
                      <DailyScheduleForm
                        checkIn={checkIn}
                        scheduleForm={scheduleForm}
                      />
                    )}
                    
                    {checkIn.cadence === 'weekly' && (
                      <WeeklyScheduleForm
                        checkIn={checkIn}
                        scheduleForm={scheduleForm}
                      />
                    )}
                    
                    {checkIn.cadence === 'monthly' && (
                      <MonthlyScheduleForm
                        checkIn={checkIn}
                        scheduleForm={scheduleForm}
                      />
                    )}
                    
                    {checkIn.cadence === 'one-off' && (
                      <OneOffScheduleForm
                        checkIn={checkIn}
                        scheduleForm={scheduleForm}
                      />
                    )}
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Save Changes</Button>
                </div>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Configuration Editor */}
      <ReportConfigEditor
        checkIn={checkIn}
        isOpen={isReportConfigDialogOpen}
        onClose={() => setIsReportConfigDialogOpen(false)}
        onConfigUpdated={(updatedConfig) => {
          // Update local checkIn state with new config
          setCheckIn(prev => prev ? { ...prev, report_config: updatedConfig } : null);
          // Refresh timeline data to show updated config summary
          fetchTimelineData();
        }}
      />

      <MessageCustomizationDialog
        isOpen={isMessageTemplateDialogOpen}
        onClose={() => setIsMessageTemplateDialogOpen(false)}
        onSave={handleMessageCustomizationSave}
        checkIn={checkIn}
        participants={participants}
        organizationName={organizationName}
        senderName={senderName}
        form={form}
        selectedPreviewParticipant={selectedPreviewParticipant}
        handlePreviewParticipantChange={handlePreviewParticipantChange}
        handleVariableInsert={handleVariableInsert}
        resetToDefaults={resetToDefaults}
      />
    </div>
  );
} 